import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../context/AuthContext'
import { API_FILE_URL, GOOGLE_API_KEY } from '../constant/api'

// ── Status mapping  (matches DB integers) ────────────────────────────────────
// 0 = suspended  |  1 = active  |  2 = pending
const STATUS_MAP = {
  0: { bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444', label: 'Suspended' },
  1: { bg: '#f0fdf4', color: '#15803d', dot: '#16a34a', label: 'Active'    },
  2: { bg: '#fffbeb', color: '#b45309', dot: '#f59e0b', label: 'Pending'   },
}
const statusColor = (s) => STATUS_MAP[s] ?? { bg: '#f3f4f6', color: '#374151', dot: '#9ca3af', label: '—' }

const FILTER_OPTIONS = [
  { label: 'All',       value: ''  },
  { label: 'Active',    value: '1' },
  { label: 'Pending',   value: '2' },
  { label: 'Suspended', value: '0' },
]

const avatarBg = (str = '') => {
  const colors = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return colors[Math.abs(h) % colors.length]
}
const initials = (name = '') => name.trim().charAt(0).toUpperCase() || '?'
const fmt      = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'
const money    = (n) => n != null ? `${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RWF` : '—'

// ── Parse product image ───────────────────────────────────────────────────────
// images is stored as a JSON array string: ["D:\\projects\\...\\file.jpg"]
// We extract the filename and build a URL to the uploads endpoint
const API_BASE = process.env.REACT_APP_API_URL || API_FILE_URL

const parseImage = (images) => {
  try {
    const arr = typeof images === 'string' ? JSON.parse(images) : images
    if (!Array.isArray(arr) || arr.length === 0) return null
    const raw = arr[0]
    if (typeof raw === 'string' && raw.startsWith('http')) return raw
    const filename = raw.replace(/\\/g, '/').split('/').pop()
    return `${API_BASE}/${filename}`
  } catch {
    return null
  }
}

// ── Shared styles ─────────────────────────────────────────────────────────────
// Build shop avatar display URL — handles disk filename, GCS URL, or null
const shopAvatarUrl = (avatar) => {
  if (!avatar) return null
  if (avatar.startsWith('http')) return avatar                  // GCS or external URL
  return `${API_BASE}/${avatar}`                        // local disk storage
}

const selectStyle = { border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }

// ── Sub-components ────────────────────────────────────────────────────────────
function StatCard({ label, value, accent }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '18px 20px', flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', color: accent || '#111317' }}>{value}</div>
    </div>
  )
}

function StatusBadge({ status }) {
  const s = statusColor(status)
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: s.bg, color: s.color, fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase' }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }} />{s.label}
    </span>
  )
}

// ── Documents Tab ─────────────────────────────────────────────────────────────
const DOC_LABELS = {
  national_id: 'National ID / Passport', business_registration: 'Business Registration',
  tin_certificate: 'TIN Certificate', bank_statement: 'Bank Statement', other: 'Other',
}

function DocumentsTab({ shop, onStatusChange }) {
  const [docs,    setDocs]    = useState([])
  const [loading, setLoading] = useState(true)
  const [saving,  setSaving]  = useState(null)
  const [note,    setNote]    = useState({})

  const reportStatus = useCallback((documents) => {
    if (!onStatusChange) return
    if (!documents.length) { onStatusChange('unverified'); return }
    const required = documents.filter(d => d.doc_type === 'national_id' || d.doc_type === 'business_registration')
    const hasRejected  = required.some(d => d.status === 'rejected')
    const hasNatId     = required.some(d => d.doc_type === 'national_id'          && d.status === 'approved')
    const hasBizReg    = required.some(d => d.doc_type === 'business_registration' && d.status === 'approved')
    onStatusChange(hasRejected ? 'rejected' : (hasNatId && hasBizReg) ? 'verified' : 'pending')
  }, [onStatusChange])

  useEffect(() => {
    setLoading(true)
    api.get(`/shops/admin/shops/${shop.shop_id}/documents`)
      .then(res => { const d = res.data.documents || []; setDocs(d); reportStatus(d) })
      .catch(() => { setDocs([]); onStatusChange?.('unverified') })
      .finally(() => setLoading(false))
  }, [shop.shop_id]) // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (doc, status) => {
    setSaving(doc.doc_id)
    try {
      const res = await api.patch(`/shops/admin/documents/${doc.doc_id}/status`, { status, admin_note: note[doc.doc_id] || '' })
      const next = docs.map(d => d.doc_id === doc.doc_id ? res.data.document : d)
      setDocs(next)
      reportStatus(next)
    } catch { /* keep optimistic */ }
    setSaving(null)
  }

  const STATUS = {
    pending:  { color: '#b45309', bg: '#fffbeb', border: '#fde68a', dot: '#f59e0b', label: 'Pending' },
    approved: { color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', dot: '#16a34a', label: 'Approved' },
    rejected: { color: '#b91c1c', bg: '#fef2f2', border: '#fecaca', dot: '#ef4444', label: 'Rejected' },
  }

  if (loading) return <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>Loading documents…</div>

  if (docs.length === 0) return (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#111317', marginBottom: 4 }}>No documents uploaded</div>
      <div style={{ fontSize: 12, color: '#9a9a94' }}>The seller hasn't uploaded any verification documents yet.</div>
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Summary badge */}
      {(() => {
        const approved = docs.filter(d => d.status === 'approved').length
        const pending  = docs.filter(d => d.status === 'pending').length
        const rejected = docs.filter(d => d.status === 'rejected').length
        return (
          <div style={{ background: approved === docs.length ? '#f0fdf4' : pending > 0 ? '#fffbeb' : '#fef2f2', border: `1px solid ${approved === docs.length ? '#bbf7d0' : pending > 0 ? '#fde68a' : '#fecaca'}`, borderRadius: 9, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>{approved === docs.length ? '✅' : pending > 0 ? '⏳' : '❌'}</span>
            <div style={{ fontSize: 12, fontWeight: 700 }}>
              {approved} approved · {pending} pending · {rejected} rejected — {docs.length} total
            </div>
          </div>
        )
      })()}

      {docs.map(doc => {
        const s = STATUS[doc.status] || STATUS.pending
        return (
          <div key={doc.doc_id} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 10, padding: '14px 16px' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 24, flexShrink: 0 }}>{doc.file_url?.toLowerCase().endsWith('.pdf') ? '📋' : '🖼'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 3 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#111317', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>{doc.file_name}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 9, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', color: s.color, background: 'rgba(255,255,255,0.7)', border: `1px solid ${s.border}`, borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap' }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }} />
                    {s.label}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: '#6b7280', fontFamily: "'JetBrains Mono',monospace" }}>
                  {DOC_LABELS[doc.doc_type] || doc.doc_type} · {new Date(doc.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
              </div>
              <a href={doc.file_url} target="_blank" rel="noreferrer"
                style={{ height: 30, padding: '0 12px', borderRadius: 6, border: `1px solid ${s.border}`, background: 'rgba(255,255,255,0.7)', color: '#374151', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
                View ↗
              </a>
            </div>

            {/* Admin note input */}
            {doc.status !== 'approved' && (
              <input
                value={note[doc.doc_id] || doc.admin_note || ''}
                onChange={e => setNote(n => ({ ...n, [doc.doc_id]: e.target.value }))}
                placeholder="Admin note (optional, shown to seller)"
                style={{ width: '100%', height: 32, border: `1px solid ${s.border}`, borderRadius: 6, padding: '0 10px', fontSize: 12, outline: 'none', marginBottom: 8, boxSizing: 'border-box', background: 'rgba(255,255,255,0.6)', fontFamily: "'Plus Jakarta Sans',sans-serif" }}
              />
            )}
            {doc.status === 'approved' && doc.admin_note && (
              <div style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic', marginBottom: 8 }}>Note: {doc.admin_note}</div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 8 }}>
              {doc.status !== 'approved' && (
                <button onClick={() => setStatus(doc, 'approved')} disabled={saving === doc.doc_id}
                  style={{ flex: 1, height: 30, borderRadius: 6, border: '1px solid #bbf7d0', background: saving === doc.doc_id ? '#d1fae5' : '#f0fdf4', color: '#15803d', fontSize: 11, fontWeight: 700, cursor: saving === doc.doc_id ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  ✓ Approve
                </button>
              )}
              {doc.status !== 'rejected' && (
                <button onClick={() => setStatus(doc, 'rejected')} disabled={saving === doc.doc_id}
                  style={{ flex: 1, height: 30, borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: saving === doc.doc_id ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  ✕ Reject
                </button>
              )}
              {doc.status === 'approved' && (
                <button onClick={() => setStatus(doc, 'pending')} disabled={saving === doc.doc_id}
                  style={{ height: 30, padding: '0 12px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', color: '#9a9a94', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  Revoke
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Seller Analytics Tab ──────────────────────────────────────────────────────
function SellerAnalyticsTab({ shopId }) {
  const [data,    setData]    = useState(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(false)
  const [period,  setPeriod]  = useState('30d')

  useEffect(() => {
    setLoading(true)
    setError(false)
    api.get(`/analytics/admin/shops/${shopId}?period=${period}`)
      .then(res => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [shopId, period])

  const periodBtnStyle = (p) => ({
    padding: '4px 10px', borderRadius: 6,
    border: `1px solid ${period === p ? '#111317' : '#e9e9e4'}`,
    background: period === p ? '#111317' : '#fff',
    color: period === p ? '#fff' : '#374151',
    fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif",
  })

  if (loading) return <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '32px 0' }}>Loading analytics…</div>

  if (error || !data) return (
    <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '32px 0' }}>
      <div style={{ fontSize: 22, marginBottom: 8 }}>📊</div>
      No analytics data available for this shop yet.
    </div>
  )

  const s = data.summary || {}
  const changePct = s.revenue_change_pct != null
    ? { val: s.revenue_change_pct, up: s.revenue_up }
    : null

  const mini = [
    { label: 'Gross Sales', value: `RWF ${Number(s.revenue   ?? 0).toLocaleString()}`, change: changePct,  accent: '#15803d' },
    { label: 'Orders',     value: s.orders    ?? 0,                                   change: null,        accent: '#111317' },
    { label: 'Avg Order',  value: `RWF ${Number(s.avg_order ?? 0).toLocaleString()}`, change: null,        accent: '#111317' },
    { label: 'Units Sold', value: s.units_sold ?? 0,                                  change: null,        accent: '#e8440a' },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Period selector */}
      <div style={{ display: 'flex', gap: 6 }}>
        {[['7d','7 Days'],['30d','30 Days'],['90d','90 Days'],['1y','1 Year']].map(([p, l]) => (
          <button key={p} onClick={() => setPeriod(p)} style={periodBtnStyle(p)}>{l}</button>
        ))}
      </div>

      {/* Mini stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {mini.map(m => (
          <div key={m.label} style={{ background: '#f9f9f7', border: '1px solid #e9e9e4', borderRadius: 9, padding: '12px 14px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', marginBottom: 6 }}>{m.label}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: m.accent, letterSpacing: '-0.3px' }}>{m.value}</div>
            {m.change && (
              <div style={{ fontSize: 10, fontWeight: 700, color: m.change.up ? '#15803d' : '#b91c1c', marginTop: 3, fontFamily: "'JetBrains Mono',monospace" }}>
                {m.change.up ? '▲' : '▼'} {m.change.val}% vs prev period
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Order payment status */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {[
          ['✅ Paid Orders',    s.paid_orders   ?? 0, '#15803d'],
          ['⏳ Unpaid Orders',  s.unpaid_orders ?? 0, '#b45309'],
          ['🔴 Live Sessions',  data.live_count ?? 0, '#111317'],
          ['▶ Clips',           data.reel_count ?? 0, '#111317'],
        ].map(([k, v, color]) => (
          <div key={k} style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 9, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>{k}</span>
            <span style={{ fontSize: 14, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color }}>{v}</span>
          </div>
        ))}
      </div>

      {/* Revenue chart — simple bar visualisation */}
      {data.revenue_chart?.length > 0 && (() => {
        const max = Math.max(...data.revenue_chart.map(r => r.value), 1)
        return (
          <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '14px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', marginBottom: 12 }}>Gross Sales — {period}</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 60 }}>
              {data.revenue_chart.map((r, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: '100%', background: '#e8440a', borderRadius: '2px 2px 0 0', opacity: 0.85, height: `${Math.max(4, Math.round((r.value / max) * 52))}px` }} />
                  <span style={{ fontSize: 8, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: '100%', textAlign: 'center' }}>{r.label}</span>
                </div>
              ))}
            </div>
          </div>
        )
      })()}

      {/* Top products */}
      {data.top_products?.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid #f3f3f0', fontSize: 11, fontWeight: 700, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>Top Products</div>
          {data.top_products.map((p, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: i < data.top_products.length - 1 ? '1px solid #f3f3f0' : 'none' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700 }}>{p.name}</div>
                <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{p.units_sold} units sold</div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>RWF {Number(p.revenue).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Shop Drawer ───────────────────────────────────────────────────────────────
function ShopDrawer({ shop, categories, deliveryZones = [], onClose, onStatusChange, onRevoke }) {
  const [tab,        setTab]        = useState('details')
  const [detail,     setDetail]     = useState(null)
  const [detailLoad, setDetailLoad] = useState(false)
  const [kycStatus,  setKycStatus]  = useState(null)   // 'verified' | 'rejected' | 'pending' | null
  const [editingShop,    setEditingShop]    = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [addingProduct,  setAddingProduct]  = useState(false)

  useEffect(() => {
    if (!shop) return
    setTab('details')
    setDetail(null)
    setKycStatus(null)
    setDetailLoad(true)
    api.get(`/shops/admin/shops/${shop.shop_id}`)
      .then(res => setDetail(res.data.shop))
      .catch(() => setDetail(shop))
      .finally(() => setDetailLoad(false))
  }, [shop?.shop_id])

  if (!shop) return null
  const d  = detail || shop
  const bg = avatarBg(d.shop_name || '')

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 200 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 580, background: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 40px rgba(0,0,0,0.12)', animation: 'slideIn 0.22s ease' }}>
        <style>{`@keyframes slideIn { from { transform:translateX(40px);opacity:0 } to { transform:translateX(0);opacity:1 } }`}</style>

        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 46, height: 46, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: '#111317', flexShrink: 0, overflow: 'hidden' }}>
            {shopAvatarUrl(d.shop_avatar)
              ? <img src={shopAvatarUrl(d.shop_avatar)} alt={d.shop_name} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => e.target.style.display='none'} />
              : initials(d.shop_name)
            }
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.shop_name}</div>
            <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>#{d.shop_id} · {d.owner_name || d.owner_phone || '—'}</div>
          </div>
          <StatusBadge status={d.status} />
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94', marginLeft: 8, lineHeight: 1 }}>×</div>
        </div>

        {/* Tabs — horizontally scrollable, no visible scrollbar */}
        <style>{`#seller-tab-bar::-webkit-scrollbar{display:none}`}</style>
        <div id="seller-tab-bar" style={{ display: 'flex', borderBottom: '1px solid #e9e9e4', overflowX: 'auto', scrollbarWidth: 'none', flexShrink: 0, padding: '0 24px' }}>
          {[
            ['details',   'Details'],
            ['products',  'Products'],
            ['zones',     'Zones'],
            ['address',   'Address'],
            ['documents', null],
            ['analytics', 'Analytics'],
          ].map(([id, label]) => {
            const docsLabel = id === 'documents'
              ? (kycStatus === 'verified'
                  ? <span>Docs <span style={{ color: '#16a34a', fontSize: 11 }}>✓</span></span>
                  : kycStatus === 'rejected'
                  ? <span>Docs <span style={{ color: '#ef4444', fontSize: 11 }}>⚠</span></span>
                  : kycStatus === 'pending'
                  ? <span>Docs <span style={{ color: '#f59e0b', fontSize: 11 }}>·</span></span>
                  : 'Docs')
              : label
            return (
              <button key={id} onClick={() => setTab(id)} style={{ padding: '12px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif", color: tab === id ? '#111317' : '#9a9a94', borderBottom: `2px solid ${tab === id ? '#e8440a' : 'transparent'}`, marginBottom: -1, whiteSpace: 'nowrap', flexShrink: 0 }}>
                {docsLabel}
              </button>
            )
          })}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {detailLoad && <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '32px 0' }}>Loading…</div>}

          {/* ── Details ── */}
          {!detailLoad && tab === 'details' && (
            <>
              {[
                ['Shop ID',        d.shop_id],
                ['Shop Name',      d.shop_name],
                ['Owner',          d.owner_name    || '—'],
                ['Owner Phone',    d.owner_phone   || '—'],
                ['Owner Email',    d.owner_email   || '—'],
                ['Owner Role',     d.owner_role    || '—'],
                ['Category',       d.category      || '—'],
                ['Location',          d.shop_location || '—'],
                ['Phone',             d.shop_phone    || '—'],
                ['TIN',               d.tin           || '—'],
                ['Fallback Fee',      d.fallback_delivery_fee != null ? `RWF ${Number(d.fallback_delivery_fee).toLocaleString()}` : '—'],
                ['Description',       d.description   || '—'],
                ['Status',            <StatusBadge status={d.status} />],
                ['Total Products', d.product_count ?? '—'],
                ['Total Orders',   d.order_count   ?? '—'],
                ['Gross Sales',     money(d.revenue)],
                ['Joined',         fmt(d.created_at)],
                ['Payout Schedule', d.payout_schedule === 'on_demand' ? '⚡ On Demand' : '📅 Monthly'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f3f0' }}>
                  <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600, flexShrink: 0, marginRight: 12 }}>{k}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#111317', textAlign: 'right', maxWidth: 280, wordBreak: 'break-word' }}>{v}</span>
                </div>
              ))}

              {/* Bank account block */}
              <div style={{ marginTop: 14, background: d.bank_account_no ? '#f0fdf4' : '#fffbeb', border: `1px solid ${d.bank_account_no ? '#bbf7d0' : '#fde68a'}`, borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: d.bank_account_no ? '#15803d' : '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 10 }}>
                  🏦 Bank Account {d.bank_account_no ? '— on file' : '— NOT SET'}
                </div>
                {d.bank_account_no ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {[
                      ['Bank',           d.bank_name       || '—'],
                      ['Account Holder', d.bank_holder     || '—'],
                      ['Account No.',    d.bank_account_no || '—'],
                      ['Branch',         d.bank_branch     || '—'],
                    ].map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>{k}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>{v}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#92400e' }}>
                    Seller has not added bank details yet — they will not appear on the payout list.
                  </div>
                )}
              </div>

            </>
          )}

          {/* ── Products ── */}
          {!detailLoad && tab === 'products' && (
            <>
              {/* Add product button */}
              <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
                <button onClick={() => setAddingProduct(true)}
                  style={{ display:'inline-flex', alignItems:'center', gap:6, height:34, padding:'0 14px', borderRadius:8, border:'none', background:'#e8440a', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                  + Add Product
                </button>
              </div>
            
              {!d.products?.length
                ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '32px 0' }}>No products yet. Add the first one!</div>
                : 
                <>{d.products.map((p, i) => {
                  const imgUrl = parseImage(p.images)
                  return (
                    <div key={p.product_id || i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f3f3f0' }}>
                      {/* Product image */}
                      <div style={{ width: 52, height: 52, borderRadius: 9, background: '#f3f3f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0, overflow: 'hidden', border: '1px solid #e9e9e4' }}>
                        {imgUrl
                          ? <img src={imgUrl} alt={p.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}
                              onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex' }} />
                          : null}
                        <span style={{ display: imgUrl ? 'none' : 'flex', fontSize:22 }}>📦</span>
                      </div>

                      {/* Name + category */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                          {p.product_type === 'quick_live' ? (
                            <span style={{ fontSize: 9, fontWeight: 800, color: '#e8440a', background: '#fff3f0', border: '1px solid #fdd0c0', borderRadius: 4, padding: '1px 6px', fontFamily: "'JetBrains Mono',monospace", flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>⚡ Quick Live</span>
                          ) : (
                            <span style={{ fontSize: 9, fontWeight: 700, color: '#6b7280', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 6px', fontFamily: "'JetBrains Mono',monospace", flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Standard</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{p.category || '—'}</div>
                      </div>

                      {/* Price */}
                      <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace" }}>{money(p.price)}</div>

                      {/* Stock badge — uses quantity from DB */}
                      <span style={{ fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '2px 7px', borderRadius: 4, background: p.quantity > 0 ? '#f0fdf4' : '#fef2f2', color: p.quantity > 0 ? '#15803d' : '#b91c1c', whiteSpace: 'nowrap' }}>
                        {p.quantity > 0 ? `${p.quantity} in stock` : 'Out of stock'}
                      </span>

                      <button onClick={() => setEditingProduct(p)}
                        style={{ padding:'3px 9px', borderRadius:6, border:'1px solid #e9e9e4', background:'#fff', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:'nowrap', flexShrink:0 }}>
                        ✏️ Edit
                      </button>
                    </div>
                  )
                })}
            </>
          }
            </>
          )}
          {/* ── Zones ── */}
          {!detailLoad && tab === 'zones' && (() => {
            const shopZoneIds = (() => {
              try { return Array.isArray(d.delivery_zones) ? d.delivery_zones : JSON.parse(d.delivery_zones || '[]') } catch { return [] }
            })()
            const assignedZones = deliveryZones.filter(z => shopZoneIds.includes(z.zone_id))
            return assignedZones.length === 0
              ? <div style={{ textAlign:'center', color:'#9a9a94', fontSize:13, padding:'40px 0' }}>
                  No delivery zones assigned.{' '}
                  <span style={{ color:'#e8440a', cursor:'pointer', fontWeight:600 }} onClick={() => setEditingShop(true)}>Edit shop</span> to add zones.
                </div>
              : <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {assignedZones.map(z => (
                    <div key={z.zone_id} style={{ padding:'12px 14px', background: z.active !== false ? '#fafaf8' : '#fef9f9', borderRadius:8, border:`1px solid ${z.active !== false ? '#e9e9e4' : '#fde8e8'}` }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                        <div style={{ width:7, height:7, borderRadius:'50%', background: z.active !== false ? '#16a34a' : '#dc2626', flexShrink:0 }} />
                        <span style={{ fontSize:13, fontWeight:700 }}>{z.zone_name}</span>
                        <span style={{ fontSize:10, fontWeight:700, color: z.active !== false ? '#16a34a' : '#dc2626', background: z.active !== false ? '#f0fdf4' : '#fef2f2', border:`1px solid ${z.active !== false ? '#bbf7d0' : '#fecaca'}`, borderRadius:4, padding:'1px 6px', fontFamily:"'JetBrains Mono',monospace" }}>
                          {z.active !== false ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                      <div style={{ fontSize:11, color:'#6b7280', fontFamily:"'JetBrains Mono',monospace", display:'flex', gap:10, flexWrap:'wrap' }}>
                        {[z.city, z.district, z.area].filter(Boolean).join(' · ') && <span>{[z.city, z.district, z.area].filter(Boolean).join(' · ')}</span>}
                        <span style={{ color:'#e8440a', fontWeight:700 }}>RWF {Number(z.fee).toLocaleString()}</span>
                        {z.delivery_time && <span>{DELIVERY_TIME_LABELS[z.delivery_time] || z.delivery_time}</span>}
                      </div>
                    </div>
                  ))}
                </div>
          })()}

          {/* ── Documents / KYC ── */}
          {!detailLoad && tab === 'documents' && <DocumentsTab shop={d} onStatusChange={setKycStatus} />}

          {/* ── Analytics ── */}
          {!detailLoad && tab === 'analytics' && <SellerAnalyticsTab shopId={d.shop_id} />}

          {/* ── Address ── */}
          {!detailLoad && tab === 'address' && (
            !d.addresses?.length
              ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No addresses on file.</div>
              : d.addresses.map((a, i) => (
                  <div key={a.address_id || i} style={{ background: '#f9f9f7', borderRadius: 10, padding: '14px 16px', marginBottom: 10, border: '1px solid #e9e9e4' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                      {a.label || `Address ${i + 1}`}
                    </div>
                    {a.formatted_address && (
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#111317', lineHeight: 1.5 }}>
                        {a.formatted_address}
                      </div>
                    )}
                    {/* Fallback individual fields if formatted_address is empty */}
                    {!a.formatted_address && [
                      ['Street',   a.street   || a.address_line1],
                      ['City',     a.city],
                      ['Province', a.province || a.state],
                      ['Country',  a.country],
                      ['Postal',   a.postal_code || a.zip],
                      ['Phone',    a.phone],
                    ].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: '#9a9a94', width: 70, flexShrink: 0 }}>{k}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#111317' }}>{v}</span>
                      </div>
                    ))}
                  </div>
                ))
              
          )}
        </div>
          
        {/* Footer actions */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e9e9e4', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {d.status === 2 && (
            <button onClick={() => onStatusChange(d, 1)} style={{ flex: 1, height: 40, borderRadius: 8, border: 'none', cursor: 'pointer', background: '#f0fdf4', color: '#15803d', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>✓ Approve</button>
          )}
          {d.status === 1 && (
            <button onClick={() => onStatusChange(d, 0)} style={{ flex: 1, height: 40, borderRadius: 8, border: 'none', cursor: 'pointer', background: '#fef2f2', color: '#b91c1c', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Suspend</button>
          )}
          {d.status === 0 && (
            <button onClick={() => onStatusChange(d, 1)} style={{ flex: 1, height: 40, borderRadius: 8, border: 'none', cursor: 'pointer', background: '#f0fdf4', color: '#15803d', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Reinstate</button>
          )}
          <button onClick={() => setEditingShop(true)}
            style={{ flex: 1, height: 40, borderRadius: 8, border: '1px solid #e9e9e4', cursor: 'pointer', background: '#fff', color: '#111317', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            ✏️ Edit Shop
          </button>
          <button onClick={() => onRevoke(d)} style={{ flex: 1, height: 40, borderRadius: 8, border: '1px solid #fecaca', cursor: 'pointer', background: '#fff5f5', color: '#b91c1c', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            Revoke Seller Role
          </button>
        </div>
      </div>

      {/* Edit Shop Modal */}
      {editingShop && (
        <EditShopModal
          shop={d}
          categories={categories}
          deliveryZones={deliveryZones}
          onClose={() => setEditingShop(false)}
          onSaved={(updated) => {
            setDetail(prev => ({ ...prev, ...updated }))
            setEditingShop(false)
          }}
        />
      )}

      {/* Edit Product Modal */}
      {editingProduct && (
        <EditProductModal
          product={editingProduct}
          categories={categories}
          onClose={() => setEditingProduct(null)}
          onSaved={(updated) => {
            setDetail(prev => ({
              ...prev,
              products: (prev?.products || []).map(p =>
                (p.prod_id || p.product_id) === (updated.prod_id || updated.product_id) ? { ...p, ...updated } : p
              ),
            }))
            setEditingProduct(null)
          }}
        />
      )}

      {/* Add Product to this shop */}
      {addingProduct && (
        <AddProductToShopModal
          shop={d}
          categories={categories}
          onClose={() => setAddingProduct(false)}
          onAdded={(newProduct) => {
            setDetail(prev => ({
              ...prev,
              products: [newProduct, ...(prev?.products || [])],
              product_count: (Number(prev?.product_count) || 0) + 1,
            }))
            setAddingProduct(false)
          }}
        />
      )}
   </>
  )
}

// ── Revoke Confirm Modal ──────────────────────────────────────────────────────
function RevokeModal({ shop, onClose, onConfirm, loading }) {
  if (!shop) return null
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 300 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 420, background: '#fff', borderRadius: 14, zIndex: 301, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn { from { opacity:0; transform:translate(-50%,-48%) scale(0.97) } to { opacity:1; transform:translate(-50%,-50%) scale(1) } }`}</style>
        <div style={{ padding: '28px 28px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Revoke Seller Role?</div>
          <div style={{ fontSize: 13, color: '#9a9a94', lineHeight: 1.7 }}>
            This will downgrade <b>{shop.owner_name || shop.owner_phone}</b> from <b>seller → buyer</b> and suspend <b>{shop.shop_name}</b>. This is done in a single transaction.
          </div>
        </div>
        <div style={{ padding: '0 28px 24px', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {loading ? 'Revoking…' : 'Yes, Revoke'}
          </button>
        </div>
      </div>
    </>
  )
}


// ── Option Row (matches seller portal) ───────────────────────────────────────
function OptionRow({ option, index, attrDefs, onChange, onDelete }) {
  const sel = { width:'100%', height:34, borderRadius:7, border:'1px solid #e9e9e4', padding:'0 8px', fontSize:12, outline:'none', background:'#fff', fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box', cursor:'pointer' }
  const num = { width:'100%', height:34, borderRadius:7, border:'1px solid #e9e9e4', padding:'0 8px', fontSize:13, outline:'none', boxSizing:'border-box' }
  const lbl = { fontSize:10, fontWeight:700, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", marginBottom:4, textTransform:'uppercase' }
  const hasAttrs = attrDefs && attrDefs.length > 0

  const handleAttrChange = (attrName, value) => {
    const attrs = { ...(option.attrs || {}), [attrName]: value }
    const label = Object.values(attrs).filter(Boolean).join(' / ')
    onChange(index, { ...option, attrs, label })
  }

  return (
    <div style={{ background:'#f9f9f7', border:'1px solid #e9e9e4', borderRadius:10, padding:'12px 14px', display:'flex', flexDirection:'column', gap:10 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <span style={{ fontSize:11, fontWeight:700, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", textTransform:'uppercase' }}>Option {index + 1}</span>
        <button type="button" onClick={() => onDelete(index)}
          style={{ border:'none', background:'#fef2f2', color:'#b91c1c', borderRadius:5, padding:'3px 9px', fontSize:11, fontWeight:700, cursor:'pointer' }}>✕</button>
      </div>

      {/* Category-based attribute selects OR free-text label */}
      {hasAttrs ? (
        <div style={{ display:'grid', gridTemplateColumns:`repeat(${Math.min(attrDefs.length, 3)}, 1fr)`, gap:8 }}>
          {attrDefs.map(attr => (
            <div key={attr.name}>
              <div style={lbl}>{attr.name}</div>
              {attr.type === 'select' && attr.options?.length ? (
                <select value={option.attrs?.[attr.name] || ''} onChange={e => handleAttrChange(attr.name, e.target.value)} style={sel}>
                  <option value="">Select {attr.name}…</option>
                  {attr.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input value={option.attrs?.[attr.name] || ''} onChange={e => handleAttrChange(attr.name, e.target.value)} placeholder={attr.name} style={sel} />
              )}
            </div>
          ))}
        </div>
      ) : (
        <div>
          <div style={lbl}>Option Label</div>
          <input value={option.label || ''} onChange={e => onChange(index, { ...option, label: e.target.value })}
            placeholder="e.g. Small / Red, XL, One size…" style={{ ...sel, cursor:'text' }} />
        </div>
      )}

      {/* Auto-composed label preview */}
      {hasAttrs && option.label && (
        <div style={{ fontSize:11, color:'#6b7280', fontFamily:"'JetBrains Mono',monospace" }}>
          Label: <b style={{ color:'#111317' }}>{option.label}</b>
        </div>
      )}

      {/* Price + Stock */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
        <div>
          <div style={lbl}>Price (RWF) *</div>
          <input type="number" min="1" value={option.price || ''}
            onChange={e => onChange(index, { ...option, price: e.target.value })}
            placeholder="0" style={num} />
        </div>
        <div>
          <div style={lbl}>Stock</div>
          <input type="number" min="0" value={option.quantity || ''}
            onChange={e => onChange(index, { ...option, quantity: e.target.value })}
            placeholder="0" style={num} />
        </div>
      </div>
    </div>
  )
}


// ── Edit Shop Modal ───────────────────────────────────────────────────────────
function EditShopModal({ shop, categories, deliveryZones = [], onClose, onSaved }) {
  const parseZoneIds = (v) => { try { return Array.isArray(v) ? v : JSON.parse(v || '[]') } catch { return [] } }
  const [form, setForm] = useState({
    shop_name:      shop.shop_name        || '',
    category:       shop.category         || '',
    description:    shop.description      || '',
    location:       shop.shop_location    || '',
    phone:          shop.shop_phone || shop.phone || '',
    tin:            shop.tin              || '',
    lat:            shop.lat  != null ? String(shop.lat) : '',
    lng:            shop.lng  != null ? String(shop.lng) : '',
    delivery_zones:        parseZoneIds(shop.delivery_zones),
    fallback_delivery_fee: shop.fallback_delivery_fee != null ? String(shop.fallback_delivery_fee) : '',
  })
  const [avatarFile,    setAvatarFile]    = useState(null)
  const [avatarPreview, setAvatarPreview] = useState(
    shop.shop_avatar
      ? (shop.shop_avatar.startsWith('http') ? shop.shop_avatar : `${API_BASE}/${shop.shop_avatar}`)
      : null
  )
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  // ── Owner search ───────────────────────────────────────────────────────────
  const [ownerSearch,     setOwnerSearch]     = useState('')
  const [ownerResults,    setOwnerResults]    = useState([])
  const [ownerSearchLoad, setOwnerSearchLoad] = useState(false)
  const [currentOwner,    setCurrentOwner]    = useState({
    user_id:  shop.owner_id   || null,
    username: shop.owner_name || '',
    phone:    shop.owner_phone || '',
  })
  const ownerTimerRef = useRef(null)

  const searchOwners = (q) => {
    clearTimeout(ownerTimerRef.current)
    if (!q || q.trim().length < 2) { setOwnerResults([]); return }
    ownerTimerRef.current = setTimeout(async () => {
      setOwnerSearchLoad(true)
      try {
        const res = await api.get(`/user/update/admin/users?search=${encodeURIComponent(q.trim())}&limit=10`)
        setOwnerResults(res.data.users || [])
      } catch { setOwnerResults([]) }
      finally { setOwnerSearchLoad(false) }
    }, 300)
  }

  // Google Maps for location
  const mapInputRef     = useRef(null)
  const autocompleteRef = useRef(null)
  const mapDivRef       = useRef(null)
  const gMapRef         = useRef(null)
  const gMarkerRef      = useRef(null)
  const [mapReady, setMapReady] = useState(false)

  useEffect(() => {
    if (!GMAP_KEY) return
    loadGoogleMaps().then(() => setMapReady(true)).catch(() => {})
  }, [])

  useEffect(() => {
    if (!mapReady || !mapInputRef.current || autocompleteRef.current) return
    const ac = new window.google.maps.places.Autocomplete(mapInputRef.current, {
      componentRestrictions: { country: 'rw' },
      fields: ['geometry', 'formatted_address', 'name'],
    })
    autocompleteRef.current = ac
    ac.addListener('place_changed', () => {
      const place = ac.getPlace()
      if (!place.geometry) return
      const lat = place.geometry.location.lat()
      const lng = place.geometry.location.lng()
      set('lat', String(lat))
      set('lng', String(lng))
      set('location', place.name || place.formatted_address?.split(',')[0] || '')
      placeMapPin(lat, lng)
    })
  }, [mapReady])

  const placeMapPin = (lat, lng) => {
    if (!mapDivRef.current || !window.google) return
    const pos = { lat: Number(lat), lng: Number(lng) }
    if (!gMapRef.current) {
      gMapRef.current = new window.google.maps.Map(mapDivRef.current, {
        center: pos, zoom: 16,
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
      })
      gMarkerRef.current = new window.google.maps.Marker({ position: pos, map: gMapRef.current })
    } else {
      gMapRef.current.setCenter(pos)
      gMarkerRef.current.setPosition(pos)
    }
  }

  useEffect(() => {
    if (form.lat && form.lng && mapDivRef.current && window.google?.maps) {
      setTimeout(() => placeMapPin(Number(form.lat), Number(form.lng)), 60)
    }
  }, [form.lat, form.lng, mapReady])

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setAvatarFile(file)
    setAvatarPreview(URL.createObjectURL(file))
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.shop_name.trim()) { setError('Shop name is required'); return }
    setSaving(true); setError('')
    try {
      const fd = new FormData()
      fd.append('name',        form.shop_name)
      fd.append('category',    form.category)
      fd.append('description', form.description)
      fd.append('location',    form.location)
      fd.append('phone',       form.phone)
      fd.append('tin',         form.tin)
      if (form.lat) fd.append('lat', form.lat)
      if (form.lng) fd.append('lng', form.lng)
      fd.append('delivery_zones', JSON.stringify(form.delivery_zones))
      if (form.fallback_delivery_fee !== '') fd.append('fallback_delivery_fee', form.fallback_delivery_fee)
      if (avatarFile) fd.append('avatar', avatarFile)
      // Send new owner_id only if it changed
      if (currentOwner.user_id && currentOwner.user_id !== shop.owner_id) {
        fd.append('owner_id', currentOwner.user_id)
      }
      const res = await api.put(`/shops/admin/shops/${shop.shop_id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      onSaved(res.data.shop)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes')
    } finally { setSaving(false) }
  }

  const INP = { width:'100%', height:38, borderRadius:8, border:'1px solid #e9e9e4', padding:'0 12px', fontSize:13, outline:'none', fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box', background:'#fff' }
  const LBL = { display:'block', fontSize:11, fontWeight:700, color:'#111317', textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'JetBrains Mono',monospace", marginBottom:6 }

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:500 }} />
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:580, maxHeight:'92vh', background:'#fff', borderRadius:16, zIndex:501, display:'flex', flexDirection:'column', boxShadow:'0 24px 80px rgba(0,0,0,0.2)', overflow:'hidden' }}>

        {/* Header */}
        <div style={{ padding:'18px 24px', borderBottom:'1px solid #e9e9e4', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0, background:'#fff' }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800 }}>✏️ Edit Shop</div>
            <div style={{ fontSize:12, color:'#9a9a94', marginTop:2, fontFamily:"'JetBrains Mono',monospace" }}>#{shop.shop_id} — {shop.shop_name}</div>
          </div>
          <div onClick={onClose} style={{ cursor:'pointer', fontSize:20, color:'#9a9a94', lineHeight:1 }}>×</div>
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:'20px 24px' }}>
          <form id="editShopForm" onSubmit={handleSave} style={{ display:'flex', flexDirection:'column', gap:16 }}>

            {/* ── Avatar ── */}
            <div>
              <label style={LBL}>Shop Avatar</label>
              <div style={{ display:'flex', alignItems:'center', gap:16, padding:'14px 16px', background:'#f9f9f7', borderRadius:10, border:'1px solid #e9e9e4' }}>
                {/* Preview */}
                <div style={{ width:80, height:80, borderRadius:14, overflow:'hidden', background:avatarBg(form.shop_name), display:'flex', alignItems:'center', justifyContent:'center', fontSize:30, fontWeight:800, flexShrink:0, border:'2px solid #e9e9e4', position:'relative' }}>
                  {avatarPreview
                    ? <img src={avatarPreview} alt="avatar" style={{ width:'100%', height:'100%', objectFit:'cover' }}
                        onError={e => { e.target.style.display='none' }} />
                    : <span>{initials(form.shop_name)}</span>
                  }
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, fontWeight:600, marginBottom:6 }}>
                    {avatarFile ? `New: ${avatarFile.name}` : (avatarPreview ? 'Current avatar' : 'No avatar — showing initials')}
                  </div>
                  <label style={{ display:'inline-flex', alignItems:'center', gap:6, background:'#e8440a', color:'#fff', padding:'6px 14px', borderRadius:7, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                    📷 {avatarFile ? 'Change' : 'Upload Avatar'}
                    <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleAvatarChange} style={{ display:'none' }} />
                  </label>
                  {avatarFile && (
                    <button type="button" onClick={() => { setAvatarFile(null); setAvatarPreview(shop.shop_avatar ? (shop.shop_avatar.startsWith('http') ? shop.shop_avatar : `${API_BASE}/${shop.shop_avatar}`) : null) }}
                      style={{ marginLeft:8, border:'none', background:'#fef2f2', color:'#b91c1c', borderRadius:6, padding:'5px 10px', fontSize:11, cursor:'pointer', fontWeight:700 }}>
                      Remove
                    </button>
                  )}
                  <div style={{ fontSize:10, color:'#9a9a94', marginTop:5 }}>JPG, PNG or WEBP · max 5 MB</div>
                </div>
              </div>
            </div>

            {/* ── Owner ── */}
            <div>
              <label style={LBL}>Shop Owner</label>
              {/* Current owner card */}
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'#f9f9f7', border:'1px solid #e9e9e4', borderRadius:10, marginBottom:8 }}>
                <div style={{ width:34, height:34, borderRadius:8, background:avatarBg(currentOwner.username||currentOwner.phone||''), display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800, flexShrink:0 }}>
                  {initials(currentOwner.username || currentOwner.phone)}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700 }}>{currentOwner.username || 'User'}</div>
                  <div style={{ display:'flex', gap:6, marginTop:2 }}>
                    <span style={{ fontSize:11, fontWeight:800, color:'#e8440a', fontFamily:"'JetBrains Mono',monospace", background:'#fff3f0', border:'1px solid #fdd0c0', padding:'1px 6px', borderRadius:4 }}>
                      📞 {currentOwner.phone}
                    </span>
                    {currentOwner.user_id !== shop.owner_id && (
                      <span style={{ fontSize:10, fontWeight:700, color:'#7c3aed', background:'#f5f3ff', padding:'1px 6px', borderRadius:4, fontFamily:"'JetBrains Mono',monospace" }}>CHANGED</span>
                    )}
                  </div>
                </div>
                {currentOwner.user_id !== shop.owner_id && (
                  <button type="button" onClick={() => { setCurrentOwner({ user_id: shop.owner_id, username: shop.owner_name || '', phone: shop.owner_phone || '' }); setOwnerSearch(''); setOwnerResults([]) }}
                    style={{ border:'none', background:'#fef2f2', color:'#b91c1c', borderRadius:6, padding:'4px 8px', fontSize:11, cursor:'pointer', fontWeight:700 }}>
                    Reset
                  </button>
                )}
              </div>

              {/* Search to change owner */}
              <div style={{ position:'relative' }}>
                <input
                  value={ownerSearch}
                  onChange={e => { setOwnerSearch(e.target.value); searchOwners(e.target.value) }}
                  placeholder="Search to change owner (phone, username…)"
                  style={{ ...INP, paddingRight: ownerSearchLoad ? 36 : 12 }}
                  autoComplete="off"
                />
                {ownerSearchLoad && <div style={{ position:'absolute', right:12, top:10, fontSize:12, color:'#9a9a94' }}>…</div>}

                {ownerResults.length > 0 && (
                  <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, boxShadow:'0 10px 30px rgba(0,0,0,0.12)', zIndex:30, maxHeight:220, overflowY:'auto' }}>
                    {ownerResults.map(u => (
                      <div key={u.user_id}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => { setCurrentOwner({ user_id: u.user_id, username: u.username || u.name || '', phone: u.phone || '' }); setOwnerSearch(''); setOwnerResults([]) }}
                        style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid #f3f3f0' }}
                        onMouseEnter={e => e.currentTarget.style.background='#f9f9f7'}
                        onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                        <div style={{ width:34, height:34, borderRadius:8, background:avatarBg(u.username||u.phone||''), display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800, flexShrink:0 }}>
                          {initials(u.username || u.phone)}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:13, fontWeight:700 }}>{u.username || u.name || 'User'}</div>
                          <div style={{ display:'flex', gap:6, marginTop:2, flexWrap:'wrap', alignItems:'center' }}>
                            <span style={{ fontSize:11, fontWeight:800, color:'#e8440a', fontFamily:"'JetBrains Mono',monospace", background:'#fff3f0', border:'1px solid #fdd0c0', padding:'1px 6px', borderRadius:4 }}>
                              📞 {u.phone}
                            </span>
                            <span style={{ fontSize:10, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>#{u.user_id} · {u.role}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Name + Category */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={LBL}>Shop Name *</label>
                <input required value={form.shop_name} onChange={e => set('shop_name', e.target.value)} style={INP} />
              </div>
              <div>
                <label style={LBL}>Category</label>
                <select value={form.category} onChange={e => set('category', e.target.value)} style={{ ...INP, cursor:'pointer' }}>
                  <option value="">Select…</option>
                  {categories.map(c => <option key={c.category_id} value={c.name}>{c.icon} {c.name}</option>)}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label style={LBL}>Description</label>
              <textarea value={form.description} onChange={e => set('description', e.target.value)}
                placeholder="Brief shop description…"
                style={{ ...INP, height:72, padding:'8px 12px', resize:'vertical' }} />
            </div>

            {/* Phone + TIN */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={LBL}>Shop Phone</label>
                <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="078XXXXXXX" style={INP} />
              </div>
              <div>
                <label style={LBL}>TIN (optional)</label>
                <input value={form.tin} onChange={e => set('tin', e.target.value)} placeholder="Tax number" style={INP} />
              </div>
            </div>

            {/* Fallback delivery fee */}
            <div>
              <label style={LBL}>Fallback Delivery Fee (RWF)</label>
              <input type="number" min="0" value={form.fallback_delivery_fee}
                onChange={e => set('fallback_delivery_fee', e.target.value)}
                placeholder="e.g. 2000 — used when buyer address has no matching zone"
                style={INP} />
              <div style={{ fontSize:11, color:'#9a9a94', marginTop:4 }}>Applied when buyer's address doesn't match any delivery zone</div>
            </div>

            {/* Location / Map */}
            <div>
              <label style={LBL}>📍 Location</label>
              {!GMAP_KEY ? (
                <input value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Kigali" style={INP} />
              ) : (
                <>
                  <input ref={mapInputRef}
                    defaultValue={form.location}
                    placeholder={mapReady ? 'Search address…' : 'Loading Google Maps…'}
                    disabled={!mapReady}
                    style={{ ...INP, color: mapReady ? '#111317' : '#9a9a94' }} />
                  {form.lat && form.lng && (
                    <>
                      <div style={{ marginTop:5, fontSize:11, color:'#15803d', fontFamily:"'JetBrains Mono',monospace" }}>
                        📍 {form.location || 'Location set'} · {Number(form.lat).toFixed(5)}, {Number(form.lng).toFixed(5)}
                      </div>
                      <div ref={mapDivRef} style={{ marginTop:8, height:180, borderRadius:10, border:'1px solid #e9e9e4', overflow:'hidden' }} />
                    </>
                  )}
                  {!form.lat && (
                    <div style={{ marginTop:5, fontSize:11, color:'#9a9a94' }}>
                      Search above to pin exact location on Google Maps
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Delivery Zones */}
            <div>
              <label style={LBL}>Delivery Zones</label>
              <ZonePicker
                zones={deliveryZones}
                selected={form.delivery_zones}
                onChange={ids => set('delivery_zones', ids)}
              />
            </div>

            {error && <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:7, padding:'9px 13px', fontSize:12, color:'#b91c1c', fontWeight:600 }}>⚠ {error}</div>}
          </form>
        </div>

        {/* Sticky footer */}
        <div style={{ padding:'14px 24px', borderTop:'1px solid #e9e9e4', display:'flex', gap:10, flexShrink:0, background:'#fff' }}>
          <button type="button" onClick={onClose}
            style={{ flex:1, height:42, borderRadius:8, border:'1px solid #e9e9e4', background:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            Cancel
          </button>
          <button type="submit" form="editShopForm" disabled={saving}
            style={{ flex:2, height:42, borderRadius:8, border:'none', background:saving?'#f0c4b4':'#e8440a', color:'#fff', fontSize:13, fontWeight:700, cursor:saving?'not-allowed':'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Edit Product Modal ────────────────────────────────────────────────────────
function EditProductModal({ product, categories, onClose, onSaved }) {
  const parseAllImages = (raw) => {
    try { const a = typeof raw === 'string' ? JSON.parse(raw) : raw; return Array.isArray(a) ? a : [] }
    catch { return [] }
  }
  const parseVariants = (raw) => {
    try { const a = typeof raw === 'string' ? JSON.parse(raw) : raw; return Array.isArray(a) && a.length ? a : [] }
    catch { return [] }
  }

  const rawImages   = parseAllImages(product.images)
  const rawVariants = parseVariants(product.variants)
  const normalize   = (v) => v?.toLowerCase().trim()

  const [form, setForm] = useState({
    name:         product.name        || '',
    description:  product.description || '',
    category:     normalize(product.category) || '',
    price:        product.price    != null ? String(product.price)    : '',
    stock:        product.quantity != null ? String(product.quantity) : '',
    has_variants: !!(product.has_variants || rawVariants.length),
    product_type: product.product_type || 'standard',
  })
  const [keptImages,  setKeptImages]  = useState(rawImages)
  const [newFiles,    setNewFiles]    = useState([])
  const [newPreviews, setNewPreviews] = useState([])
  const [variants,    setVariants]    = useState(
    rawVariants.length
      ? rawVariants.map(v => {
          const attrs = typeof v.attributes === 'string' ? JSON.parse(v.attributes) : (v.attributes || {})
          return {
            variant_id: v.variant_id || null,
            attrs,
            label: attrs.label || Object.values(attrs).filter(Boolean).join(' / ') || '',
            price:    String(v.price || ''),
            quantity: String(v.quantity ?? ''),
          }
        })
      : [{ attrs:{}, label:'', price:'', quantity:'' }]
  )
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const totalImages = keptImages.length + newFiles.length

  // Option helpers
  const addVariant    = () => setVariants(v => [...v, { attrs:{}, label:'', price:'', quantity:'' }])
  const updateVariant = (i, val) => setVariants(v => v.map((x, idx) => idx === i ? val : x))
  const deleteVariant = (i) => setVariants(v => v.filter((_, idx) => idx !== i))

  const selCat = categories.find(cat => cat.name?.toLowerCase().trim() === form.category)
  const attrDefs = (() => {
    try { return Array.isArray(selCat?.attribute_defs) ? selCat.attribute_defs : JSON.parse(selCat?.attribute_defs || '[]') }
    catch { return [] }
  })()

  const handleNewFiles = (e) => {
    const files   = Array.from(e.target.files || [])
    const allowed = 5 - keptImages.length - newFiles.length
    const toAdd   = files.slice(0, allowed)
    setNewFiles(prev    => [...prev, ...toAdd])
    setNewPreviews(prev => [...prev, ...toAdd.map(f => URL.createObjectURL(f))])
    e.target.value = ''
  }
  const removeKept = (path) => setKeptImages(prev => prev.filter(p => p !== path))
  const removeNew  = (i) => {
    URL.revokeObjectURL(newPreviews[i])
    setNewFiles(prev    => prev.filter((_, idx) => idx !== i))
    setNewPreviews(prev => prev.filter((_, idx) => idx !== i))
  }

  const imgDisplayUrl = (src) => {
    if (!src) return null
    if (src.startsWith('http')) return src                                    // GCS URL
    return `${API_BASE}/${src.replace(/\\/g, '/').split('/').pop()}` // local disk
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.name.trim())  { setError('Product name is required'); return }
    if (totalImages === 0)  { setError('At least one image is required'); return }
    if (!form.has_variants) {
      if (!form.price || Number(form.price) <= 0) { setError('Valid price is required'); return }
    } else {
      if (!variants.length) { setError('Add at least one option.'); return }
      if (variants.some(v => !v.label?.trim()))                  { setError('Every option needs a label.'); return }
      if (variants.some(v => !v.price || Number(v.price) <= 0)) { setError('Every option needs a valid price.'); return }
    }
    setSaving(true); setError('')
    try {
      const fd = new FormData()
      fd.append('name',           form.name.trim())
      fd.append('description',    form.description)
      fd.append('category',       form.category)
      fd.append('has_variants',   form.has_variants)
      fd.append('product_type',   form.product_type)
      if (!form.has_variants) {
        fd.append('price', form.price)
        fd.append('stock', form.stock || '0')
      } else {
        fd.append('variants', JSON.stringify(variants.map(v => ({
          variant_id: v.variant_id || null,
          attributes: { label: v.label?.trim() || '' },
          price:      Number(v.price),
          quantity:   Number(v.quantity || 0),
        }))))
      }
      fd.append('existingImages', JSON.stringify(keptImages))
      newFiles.forEach(f => fd.append('images', f))
      const res = await api.put(`/shops/admin/products/${product.prod_id || product.product_id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      onSaved(res.data.product)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes')
    } finally { setSaving(false) }
  }

  const INP = { width:'100%', height:38, borderRadius:8, border:'1px solid #e9e9e4', padding:'0 12px', fontSize:13, outline:'none', fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box', background:'#fff' }
  const LBL = { display:'block', fontSize:11, fontWeight:700, color:'#111317', textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'JetBrains Mono',monospace", marginBottom:6 }

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:500 }} />
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:580, maxHeight:'92vh', background:'#fff', borderRadius:16, zIndex:501, display:'flex', flexDirection:'column', boxShadow:'0 24px 80px rgba(0,0,0,0.2)', overflow:'hidden' }}>

        {/* Header */}
        <div style={{ padding:'18px 24px', borderBottom:'1px solid #e9e9e4', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0, background:'#fff' }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800 }}>✏️ Edit Product</div>
            <div style={{ fontSize:12, color:'#9a9a94', marginTop:2, fontFamily:"'JetBrains Mono',monospace" }}>
              #{product.prod_id || product.product_id} — {product.name}
              {form.has_variants && <span style={{ marginLeft:8, background:'#f5f3ff', color:'#7c3aed', padding:'1px 7px', borderRadius:4, fontSize:10, fontWeight:700, fontFamily:"'JetBrains Mono',monospace" }}>HAS OPTIONS</span>}
            </div>
          </div>
          <div onClick={onClose} style={{ cursor:'pointer', fontSize:20, color:'#9a9a94', lineHeight:1 }}>×</div>
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:'20px 24px' }}>
          <form id="editProdForm" onSubmit={handleSave} style={{ display:'flex', flexDirection:'column', gap:16 }}>

            {/* ── Images ── */}
            <div>
              <label style={LBL}>
                Product Images *
                <span style={{ marginLeft:8, fontSize:10, fontWeight:700, fontFamily:"'JetBrains Mono',monospace",
                  color: totalImages === 0 ? '#b91c1c' : totalImages < 3 ? '#b45309' : '#15803d' }}>
                  {totalImages}/5{totalImages === 0 ? ' — required' : ''}
                </span>
              </label>
              <div style={{ background:'#f9f9f7', border:`1px solid ${totalImages===0?'#fecaca':'#e9e9e4'}`, borderRadius:10, padding:'14px' }}>
                {(keptImages.length > 0 || newPreviews.length > 0) && (
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
                    {keptImages.map((src, i) => (
                      <div key={`k${i}`} style={{ position:'relative' }}>
                        <img src={imgDisplayUrl(src)} alt=""
                          style={{ width:72, height:72, borderRadius:8, objectFit:'cover', border:'1.5px solid #e9e9e4', display:'block' }}
                          onError={e => { e.target.src=''; e.target.style.background='#f3f3f0' }} />
                        <button type="button" onClick={() => removeKept(src)}
                          style={{ position:'absolute', top:-6, right:-6, width:20, height:20, borderRadius:'50%', border:'none', background:'#ef4444', color:'#fff', fontSize:11, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900 }}>×</button>
                      </div>
                    ))}
                    {newPreviews.map((url, i) => (
                      <div key={`n${i}`} style={{ position:'relative' }}>
                        <img src={url} alt="" style={{ width:72, height:72, borderRadius:8, objectFit:'cover', border:'2px solid #e8440a', display:'block' }} />
                        <button type="button" onClick={() => removeNew(i)}
                          style={{ position:'absolute', top:-6, right:-6, width:20, height:20, borderRadius:'50%', border:'none', background:'#ef4444', color:'#fff', fontSize:11, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900 }}>×</button>
                        <div style={{ position:'absolute', bottom:2, left:2, background:'#e8440a', color:'#fff', fontSize:8, fontWeight:700, borderRadius:3, padding:'1px 4px' }}>NEW</div>
                      </div>
                    ))}
                  </div>
                )}
                {totalImages < 5 && (
                  <label style={{ display:'inline-flex', alignItems:'center', gap:6, background: totalImages===0 ? '#e8440a' : '#f3f3f0', color: totalImages===0 ? '#fff' : '#374151', padding:'7px 14px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer', border: totalImages===0 ? 'none' : '1px dashed #d1d5db' }}>
                    📷 {totalImages===0 ? 'Add Images *' : `Add More (${5-totalImages} left)`}
                    <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp" multiple onChange={handleNewFiles} style={{ display:'none' }} />
                  </label>
                )}
                {totalImages === 0 && <div style={{ marginTop:8, fontSize:11, color:'#b91c1c', fontWeight:600 }}>⚠ At least one image is required to save</div>}
              </div>
            </div>

            {/* Name */}
            <div>
              <label style={LBL}>Product Name *</label>
              <input required value={form.name} onChange={e => set('name', e.target.value)} style={INP} />
            </div>

            {/* Description */}
            <div>
              <label style={LBL}>Description</label>
              <textarea value={form.description} onChange={e => set('description', e.target.value)}
                placeholder="Product description…"
                style={{ ...INP, height:68, padding:'8px 12px', resize:'vertical' }} />
            </div>

            {/* Category */}
            <div>
              <label style={LBL}>Category</label>
              <select
  value={form.category}
  onChange={e => set('category', e.target.value)}
  style={{ ...INP, cursor:'pointer' }}
>
  <option value="">Select category…</option>
  {categories.map(cat => (
    <option
      key={cat.category_id}
      value={normalize(cat.name)}
    >
      {cat.icon} {cat.name}
    </option>
  ))}
</select>
            </div>

            {/* Listing type */}
            <div>
              <label style={LBL}>Listing Type</label>
              <div style={{ display:'flex', gap:8 }}>
                {[
                  { val:'standard',   label:'📦 Standard',   desc:'Regular shop product' },
                  { val:'quick_live', label:'⚡ Quick Live', desc:'Sold during live — requires seller confirm' },
                ].map(opt => (
                  <div key={opt.val} onClick={() => set('product_type', opt.val)}
                    style={{ flex:1, padding:'10px 12px', borderRadius:10, border:`1.5px solid ${form.product_type===opt.val?'#e8440a':'#e9e9e4'}`, background:form.product_type===opt.val?'#fff3f0':'#fff', cursor:'pointer', textAlign:'center' }}>
                    <div style={{ fontSize:13, fontWeight:700, color:form.product_type===opt.val?'#e8440a':'#374151' }}>{opt.label}</div>
                    <div style={{ fontSize:11, color:'#9a9a94', marginTop:2 }}>{opt.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Product structure */}
            <div>
              <label style={LBL}>Product Structure</label>
              <div style={{ display:'flex', gap:8 }}>
                {[
                  { val:false, label:'📦 Single Product', desc:'One price + stock' },
                  { val:true,  label:'🎨 Has Options',    desc:'Sizes / colors etc.' },
                ].map(opt => (
                  <div key={String(opt.val)} onClick={() => set('has_variants', opt.val)}
                    style={{ flex:1, padding:'10px 12px', borderRadius:10, border:`1.5px solid ${form.has_variants===opt.val?'#e8440a':'#e9e9e4'}`, background:form.has_variants===opt.val?'#fff3f0':'#fff', cursor:'pointer', textAlign:'center' }}>
                    <div style={{ fontSize:13, fontWeight:700, color:form.has_variants===opt.val?'#e8440a':'#374151' }}>{opt.label}</div>
                    <div style={{ fontSize:11, color:'#9a9a94', marginTop:2 }}>{opt.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Single product: price + stock */}
            {!form.has_variants && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={LBL}>Price (RWF) *</label>
                  <input type="number" min="1" required={!form.has_variants} value={form.price} onChange={e => set('price', e.target.value)} style={INP} />
                </div>
                <div>
                  <label style={LBL}>Stock Quantity</label>
                  <input type="number" min="0" value={form.stock} onChange={e => set('stock', e.target.value)} style={INP} />
                </div>
              </div>
            )}

            {/* Variants */}
            {form.has_variants && (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <label style={{ ...LBL, marginBottom:0 }}>Options ({variants.length})</label>
                  {variants.length > 0 && (
                    <span style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>
                      Total stock: {variants.reduce((s,v) => s + Number(v.quantity||0), 0)} units
                    </span>
                  )}
                </div>
                {attrDefs.length === 0 && (
                  <div style={{ background:'#fff3f0', border:'1px solid #fdd0c0', borderRadius:8, padding:'10px 13px', fontSize:12, color:'#92400e' }}>
                    ℹ No attribute definitions for this category. Use free-text labels or set them in <b>Settings → Categories</b>.
                  </div>
                )}
                {variants.map((v, i) => (
                  <OptionRow key={v.variant_id || i} option={v} index={i} attrDefs={attrDefs} onChange={updateVariant} onDelete={deleteVariant} />
                ))}
                <button type="button" onClick={addVariant}
                  style={{ height:36, borderRadius:7, border:'1px dashed #e8440a', background:'transparent', color:'#e8440a', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                  + Add Option
                </button>
              </div>
            )}

            {error && <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:7, padding:'9px 13px', fontSize:12, color:'#b91c1c', fontWeight:600 }}>⚠ {error}</div>}
          </form>
        </div>

        {/* Sticky footer */}
        <div style={{ padding:'14px 24px', borderTop:'1px solid #e9e9e4', display:'flex', gap:10, flexShrink:0, background:'#fff' }}>
          <button type="button" onClick={onClose}
            style={{ flex:1, height:42, borderRadius:8, border:'1px solid #e9e9e4', background:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            Cancel
          </button>
          <button type="submit" form="editProdForm" disabled={saving || totalImages === 0}
            style={{ flex:2, height:42, borderRadius:8, border:'none', background:(saving||totalImages===0)?'#f0c4b4':'#e8440a', color:'#fff', fontSize:13, fontWeight:700, cursor:(saving||totalImages===0)?'not-allowed':'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            {saving ? 'Saving…' : 'Save Product'}
          </button>
        </div>
      </div>
    </>
  )
}



// ── Add Product To Shop Modal ─────────────────────────────────────────────────
// Opened from ShopDrawer → Products tab → "+ Add Product" button.
// Mirrors the CreateShopModal step-2 form but for an existing shop.
function AddProductToShopModal({ shop, categories, onClose, onAdded }) {
  const [form, setForm] = useState({
    name: '', description: '', category: '', price: '', stock: '', has_variants: false, product_type: 'standard',
  })
  const [options,    setOptions]    = useState([{ label:'', attrs:{}, price:'', quantity:'' }])
  const [imageFiles, setImageFiles] = useState([])
  const [previews,   setPreviews]   = useState([])
  const [saving,     setSaving]     = useState(false)
  const [error,      setError]      = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const prevCatRef = useRef(form.category)

  const addOption    = () => setOptions(o => [...o, { label:'', attrs:{}, price:'', quantity:'' }])
  const updateOption = (i, val) => setOptions(o => o.map((x, idx) => idx === i ? val : x))
  const deleteOption = (i) => setOptions(o => o.filter((_, idx) => idx !== i))

  const selCat = categories.find(c => c.name === form.category)
  const attrDefs = (() => {
    try { return Array.isArray(selCat?.attribute_defs) ? selCat.attribute_defs : JSON.parse(selCat?.attribute_defs || '[]') }
    catch { return [] }
  })()

  // Reset options when category changes so stale attribute values don't carry over
  useEffect(() => {
    if (prevCatRef.current !== form.category) {
      prevCatRef.current = form.category
      setOptions([{ label:'', attrs:{}, price:'', quantity:'' }])
    }
  }, [form.category])

  const handleImages = (e) => {
    const files   = Array.from(e.target.files || [])
    const toAdd   = files.slice(0, 5 - imageFiles.length)
    setImageFiles(prev => [...prev, ...toAdd])
    setPreviews(prev => [...prev, ...toAdd.map(f => URL.createObjectURL(f))])
    e.target.value = ''
  }
  const removeImg = (i) => {
    URL.revokeObjectURL(previews[i])
    setImageFiles(prev => prev.filter((_, idx) => idx !== i))
    setPreviews(prev    => prev.filter((_, idx) => idx !== i))
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.name.trim())                                                   { setError('Product name is required.'); return }
    if (!form.description.trim())                                            { setError('Product description is required.'); return }
    if (imageFiles.length === 0)                                             { setError('At least one image is required.'); return }
    if (!form.has_variants) {
      if (!form.price || Number(form.price) <= 0)                           { setError('A valid price is required.'); return }
    } else {
      if (!options.length)                                                   { setError('Add at least one option.'); return }
      if (options.some(o => !o.label?.trim()))                              { setError('Every option needs a label.'); return }
      if (options.some(o => !o.price || Number(o.price) <= 0))             { setError('Every option needs a valid price.'); return }
    }
    setSaving(true); setError('')
    try {
      const fd = new FormData()
      fd.append('shop_id',      shop.shop_id)
      fd.append('name',         form.name.trim())
      fd.append('description',  form.description.trim())
      fd.append('category',     form.category)
      fd.append('has_variants', String(form.has_variants))
      fd.append('product_type', form.product_type)
      if (!form.has_variants) {
        fd.append('price', Number(form.price))
        fd.append('stock', Number(form.stock) || 0)
      } else {
        const minPrice   = Math.min(...options.map(o => Number(o.price) || 0))
        const totalStock = options.reduce((s, o) => s + (Number(o.quantity) || 0), 0)
        fd.append('price',    minPrice)
        fd.append('stock',    totalStock)
        fd.append('variants', JSON.stringify(options.map(o => ({
          attributes: { label: o.label.trim() },
          price:      Number(o.price)    || 0,
          quantity:   Number(o.quantity) || 0,
        }))))
      }
      imageFiles.forEach(f => fd.append('images', f))
      const res = await api.post('/products/admin/add', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      onAdded(res.data.product)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add product')
    } finally { setSaving(false) }
  }

  const INP = { width:'100%', height:38, borderRadius:8, border:'1px solid #e9e9e4', padding:'0 12px', fontSize:13, outline:'none', fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box', background:'#fff' }
  const LBL = { display:'block', fontSize:11, fontWeight:700, color:'#111317', textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'JetBrains Mono',monospace", marginBottom:6 }

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:600 }} />
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:560, maxHeight:'92vh', background:'#fff', borderRadius:16, zIndex:601, display:'flex', flexDirection:'column', boxShadow:'0 24px 80px rgba(0,0,0,0.2)', overflow:'hidden' }}>

        {/* Header */}
        <div style={{ padding:'18px 24px', borderBottom:'1px solid #e9e9e4', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800 }}>📦 Add Product</div>
            <div style={{ fontSize:12, color:'#9a9a94', marginTop:2, fontFamily:"'JetBrains Mono',monospace" }}>
              → {shop.shop_name}
            </div>
          </div>
          <div onClick={onClose} style={{ cursor:'pointer', fontSize:20, color:'#9a9a94', lineHeight:1 }}>×</div>
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:'20px 24px' }}>
          <form id="addProdShopForm" onSubmit={handleSave} style={{ display:'flex', flexDirection:'column', gap:16 }}>

            {/* Images — mandatory */}
            <div>
              <label style={LBL}>
                Images *
                <span style={{ marginLeft:8, fontSize:10, fontWeight:700, fontFamily:"'JetBrains Mono',monospace",
                  color: imageFiles.length === 0 ? '#b91c1c' : '#15803d' }}>
                  {imageFiles.length}/5{imageFiles.length === 0 ? ' — required' : ''}
                </span>
              </label>
              <div style={{ background:'#f9f9f7', border:`1px solid ${imageFiles.length===0?'#fecaca':'#e9e9e4'}`, borderRadius:10, padding:'14px' }}>
                {previews.length > 0 && (
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
                    {previews.map((url, i) => (
                      <div key={i} style={{ position:'relative' }}>
                        <img src={url} alt="" style={{ width:72, height:72, borderRadius:8, objectFit:'cover', border:'1.5px solid #e9e9e4', display:'block' }} />
                        <button type="button" onClick={() => removeImg(i)}
                          style={{ position:'absolute', top:-6, right:-6, width:20, height:20, borderRadius:'50%', border:'none', background:'#ef4444', color:'#fff', fontSize:11, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
                {imageFiles.length < 5 && (
                  <label style={{ display:'inline-flex', alignItems:'center', gap:6, background: imageFiles.length===0 ? '#e8440a' : '#f3f3f0', color: imageFiles.length===0 ? '#fff' : '#374151', padding:'7px 14px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer', border: imageFiles.length===0 ? 'none' : '1px dashed #d1d5db' }}>
                    📷 {imageFiles.length === 0 ? 'Add Images *' : `Add More (${5-imageFiles.length} left)`}
                    <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp" multiple onChange={handleImages} style={{ display:'none' }} />
                  </label>
                )}
                {imageFiles.length === 0 && <div style={{ marginTop:8, fontSize:11, color:'#b91c1c', fontWeight:600 }}>⚠ At least one image is required</div>}
              </div>
            </div>

            {/* Name */}
            <div>
              <label style={LBL}>Product Name *</label>
              <input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Summer Dress" style={INP} />
            </div>

            {/* Description */}
            <div>
              <label style={LBL}>Description *</label>
              <textarea value={form.description} onChange={e => set('description', e.target.value)}
                placeholder="Describe the product — material, size, colour, usage…"
                style={{ ...INP, height:72, padding:'8px 12px', resize:'vertical' }} />
            </div>

            {/* Category */}
            <div>
              <label style={LBL}>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} style={{ ...INP, cursor:'pointer' }}>
                <option value="">Select category…</option>
                {categories.map(cat => <option key={cat.category_id} value={cat.name}>{cat.icon} {cat.name}</option>)}
              </select>
            </div>

            {/* Listing type */}
            <div>
              <label style={LBL}>Listing Type</label>
              <div style={{ display:'flex', gap:8 }}>
                {[
                  { val:'standard',   label:'📦 Standard',   desc:'Regular shop product' },
                  { val:'quick_live', label:'⚡ Quick Live', desc:'Sold during live — requires seller confirm' },
                ].map(opt => (
                  <div key={opt.val} onClick={() => set('product_type', opt.val)}
                    style={{ flex:1, padding:'10px 12px', borderRadius:10, border:`1.5px solid ${form.product_type===opt.val?'#e8440a':'#e9e9e4'}`, background:form.product_type===opt.val?'#fff3f0':'#fff', cursor:'pointer', textAlign:'center', transition:'all 0.12s' }}>
                    <div style={{ fontSize:13, fontWeight:700, color:form.product_type===opt.val?'#e8440a':'#374151' }}>{opt.label}</div>
                    <div style={{ fontSize:11, color:'#9a9a94', marginTop:2 }}>{opt.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Product structure */}
            <div>
              <label style={LBL}>Product Structure</label>
              <div style={{ display:'flex', gap:8 }}>
                {[
                  { val:false, label:'📦 Single Product', desc:'One price + stock' },
                  { val:true,  label:'🎨 Has Options',    desc:'Sizes / colors etc.' },
                ].map(opt => (
                  <div key={String(opt.val)} onClick={() => set('has_variants', opt.val)}
                    style={{ flex:1, padding:'10px 12px', borderRadius:10, border:`1.5px solid ${form.has_variants===opt.val?'#e8440a':'#e9e9e4'}`, background:form.has_variants===opt.val?'#fff3f0':'#fff', cursor:'pointer', textAlign:'center', transition:'all 0.12s' }}>
                    <div style={{ fontSize:13, fontWeight:700, color:form.has_variants===opt.val?'#e8440a':'#374151' }}>{opt.label}</div>
                    <div style={{ fontSize:11, color:'#9a9a94', marginTop:2 }}>{opt.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Single product: price + stock */}
            {!form.has_variants && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={LBL}>Price (RWF) *</label>
                  <input type="number" min="1" required value={form.price} onChange={e => set('price', e.target.value)} placeholder="0" style={INP} />
                </div>
                <div>
                  <label style={LBL}>Stock Quantity</label>
                  <input type="number" min="0" value={form.stock} onChange={e => set('stock', e.target.value)} placeholder="0" style={INP} />
                </div>
              </div>
            )}

            {/* Options */}
            {form.has_variants && (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <label style={{ ...LBL, marginBottom:0 }}>
                    Options ({options.length})
                    {attrDefs.length > 0 && (
                      <span style={{ marginLeft:8, fontSize:10, fontWeight:600, color:'#e8440a', textTransform:'none', letterSpacing:0 }}>
                        — based on {selCat?.name} attributes
                      </span>
                    )}
                  </label>
                  <span style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>
                    Total stock: {options.reduce((s, o) => s + (Number(o.quantity) || 0), 0)} units
                  </span>
                </div>
                {!form.category && (
                  <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, padding:'9px 13px', fontSize:12, color:'#92400e' }}>
                    ℹ Select a category above to see attribute-based option selectors.
                  </div>
                )}
                {options.map((o, i) => (
                  <OptionRow key={i} option={o} index={i} attrDefs={attrDefs} onChange={updateOption} onDelete={deleteOption} />
                ))}
                <button type="button" onClick={addOption}
                  style={{ height:34, borderRadius:7, border:'1px dashed #e8440a', background:'transparent', color:'#e8440a', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                  + Add Option
                </button>
              </div>
            )}

            {error && <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:7, padding:'9px 13px', fontSize:12, color:'#b91c1c', fontWeight:600 }}>⚠ {error}</div>}
          </form>
        </div>

        {/* Sticky footer */}
        <div style={{ padding:'14px 24px', borderTop:'1px solid #e9e9e4', display:'flex', gap:10, flexShrink:0, background:'#fff' }}>
          <button type="button" onClick={onClose}
            style={{ flex:1, height:42, borderRadius:8, border:'1px solid #e9e9e4', background:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            Cancel
          </button>
          <button type="submit" form="addProdShopForm" disabled={saving || imageFiles.length === 0}
            style={{ flex:2, height:42, borderRadius:8, border:'none', background:(saving||imageFiles.length===0)?'#f0c4b4':'#e8440a', color:'#fff', fontSize:13, fontWeight:700, cursor:(saving||imageFiles.length===0)?'not-allowed':'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            {saving ? 'Adding…' : '+ Add Product'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Google Maps loader ───────────────────────────────────────────────────────
// Loads the Maps JS API once and caches the promise so it is never double-loaded
const DELIVERY_TIME_LABELS = {
  same_day : 'Same Day',
  next_day : 'Next Day',
  '2-3_days': '2–3 Days',
  '3-5_days': '3–5 Days',
  '1_week'  : '1 Week',
}

function ZonePicker({ zones, selected = [], onChange }) {
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter(z => z !== id) : [...selected, id])
  if (!zones.length) return (
    <div style={{ fontSize:12, color:'#9a9a94', padding:'10px 0' }}>
      No delivery zones configured. Add them in <b>Settings → Delivery Zones</b>.
    </div>
  )
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
      {zones.map(z => {
        const sel = selected.includes(z.zone_id)
        const active = z.active !== false
        return (
          <div key={z.zone_id} onClick={() => toggle(z.zone_id)}
            style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:8, border:`1.5px solid ${sel?'#e8440a':'#e9e9e4'}`, background:sel?'#fff3f0':'#f9f9f7', cursor:'pointer', transition:'all 0.12s' }}>
            <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${sel?'#e8440a':'#c4c4c0'}`, background:sel?'#e8440a':'#fff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              {sel && <span style={{ color:'#fff', fontSize:11, lineHeight:1, fontWeight:900 }}>✓</span>}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                <span style={{ fontSize:13, fontWeight:700 }}>{z.zone_name}</span>
                {!active && <span style={{ fontSize:9, fontWeight:700, color:'#dc2626', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:3, padding:'1px 5px', fontFamily:"'JetBrains Mono',monospace" }}>INACTIVE</span>}
              </div>
              <div style={{ fontSize:11, color:'#6b7280', fontFamily:"'JetBrains Mono',monospace", marginTop:2, display:'flex', gap:8, flexWrap:'wrap' }}>
                {[z.city, z.district, z.area].filter(Boolean).join(' · ') && (
                  <span>{[z.city, z.district, z.area].filter(Boolean).join(' · ')}</span>
                )}
                <span style={{ color:'#e8440a', fontWeight:700 }}>RWF {Number(z.fee).toLocaleString()}</span>
                {z.delivery_time && <span>{DELIVERY_TIME_LABELS[z.delivery_time] || z.delivery_time}</span>}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

const GMAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || GOOGLE_API_KEY || ''
let _gmapsPromise = null
function loadGoogleMaps() {
  if (window.google?.maps?.places) return Promise.resolve()
  if (_gmapsPromise) return _gmapsPromise
  _gmapsPromise = new Promise((resolve, reject) => {
    const s  = document.createElement('script')
    s.src    = `https://maps.googleapis.com/maps/api/js?key=${GMAP_KEY}&libraries=places`
    s.async  = true
    s.defer  = true
    s.onload = resolve
    s.onerror = () => { _gmapsPromise = null; reject(new Error('Google Maps failed to load')) }
    document.head.appendChild(s)
  })
  return _gmapsPromise
}

// ── Create Shop Modal ─────────────────────────────────────────────────────────
function CreateShopModal({ categories, deliveryZones = [], onClose, onCreated }) {
  const [step,        setStep]        = useState(1)
  const [saving,      setSaving]      = useState(false)
  const [error,       setError]       = useState('')
  const [createdShop, setCreatedShop] = useState(null)

  // ── Shop form state ────────────────────────────────────────────────────────
  const [shopForm, setShopForm] = useState({
    owner_search: '', owner_id: null, owner_name: '', owner_phone: '', owner_id_display: '',
    shop_name: '', category: '', description: '', phone: '', tin: '',
    // Map / address
    map_query: '', lat: '', lng: '', place_name: '',
    address_line1: '', city: 'Kigali', province: 'Kigali City', country: 'Rwanda',
    // Delivery
    delivery_zones: [], fallback_delivery_fee: '',
  })
  const setShop = (k, v) => setShopForm(f => ({ ...f, [k]: v }))

  // ── Owner search with debounce ─────────────────────────────────────────────
  const [userResults,    setUserResults]    = useState([])
  const [userSearchLoad, setUserSearchLoad] = useState(false)
  const searchTimerRef = useRef(null)

  const searchUsers = (q) => {
    clearTimeout(searchTimerRef.current)
    if (!q || q.trim().length < 2) { setUserResults([]); return }
    searchTimerRef.current = setTimeout(async () => {
      setUserSearchLoad(true)
      try {
        const res = await api.get(`/user/update/admin/users?search=${encodeURIComponent(q.trim())}&limit=10`)
        setUserResults(res.data.users || [])
      } catch { setUserResults([]) }
      finally { setUserSearchLoad(false) }
    }, 300)
  }

  // ── Google Maps Places autocomplete ───────────────────────────────────────
  const mapInputRef    = useRef(null)
  const autocompleteRef = useRef(null)
  const mapPreviewRef  = useRef(null)
  const googleMapRef   = useRef(null)
  const markerRef      = useRef(null)
  const [mapReady,  setMapReady]  = useState(false)
  const [mapError,  setMapError]  = useState('')

  // Load Google Maps when the map section mounts
  useEffect(() => {
    if (!GMAP_KEY) {
      setMapError('REACT_APP_GOOGLE_MAPS_KEY is not set. Add it to your .env file.')
      return
    }
    loadGoogleMaps()
      .then(() => setMapReady(true))
      .catch(() => setMapError('Could not load Google Maps. Check your API key.'))
  }, [])

  // Attach Places Autocomplete once map is ready and input is mounted
  useEffect(() => {
    if (!mapReady || !mapInputRef.current || autocompleteRef.current) return
    const ac = new window.google.maps.places.Autocomplete(mapInputRef.current, {
      componentRestrictions: { country: 'rw' },
      fields: ['geometry', 'formatted_address', 'name', 'address_components'],
    })
    autocompleteRef.current = ac
    ac.addListener('place_changed', () => {
      const place = ac.getPlace()
      if (!place.geometry) return
      const lat = place.geometry.location.lat()
      const lng = place.geometry.location.lng()
      const formatted = place.formatted_address || place.name || ''
      // Extract city from address components
      const cityComp = place.address_components?.find(c =>
        c.types.includes('locality') || c.types.includes('administrative_area_level_2')
      )
      setShopForm(f => ({
        ...f,
        lat:           String(lat),
        lng:           String(lng),
        place_name:    formatted,
        map_query:     formatted,
        address_line1: place.name || formatted.split(',')[0] || '',
        city:          cityComp?.long_name || 'Kigali',
      }))
      // Update/create map preview
      updateMapPreview(lat, lng)
    })
  }, [mapReady])

  // Render Google Map preview
  const updateMapPreview = (lat, lng) => {
    if (!mapPreviewRef.current || !window.google) return
    const pos = { lat: Number(lat), lng: Number(lng) }
    if (!googleMapRef.current) {
      googleMapRef.current = new window.google.maps.Map(mapPreviewRef.current, {
        center: pos, zoom: 16,
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
        zoomControl: true,
        styles: [{ featureType:'poi',elementType:'labels',stylers:[{visibility:'off'}] }],
      })
      markerRef.current = new window.google.maps.Marker({
        position: pos, map: googleMapRef.current,
        animation: window.google.maps.Animation.DROP,
      })
    } else {
      googleMapRef.current.setCenter(pos)
      markerRef.current.setPosition(pos)
    }
  }

  // Re-render map when preview div mounts after lat/lng is set
  useEffect(() => {
    if (shopForm.lat && shopForm.lng && mapPreviewRef.current && window.google?.maps) {
      setTimeout(() => updateMapPreview(Number(shopForm.lat), Number(shopForm.lng)), 50)
    }
  }, [shopForm.lat, shopForm.lng])

  const clearLocation = () => {
    setShopForm(f => ({ ...f, lat:'', lng:'', place_name:'', map_query:'', address_line1:'' }))
    if (mapInputRef.current) mapInputRef.current.value = ''
    googleMapRef.current = null
    markerRef.current    = null
  }

  // ── handleCreateShop ───────────────────────────────────────────────────────
  const handleCreateShop = async (e) => {
    e.preventDefault()
    if (!shopForm.owner_id)       { setError('Select an owner first'); return }
    if (!shopForm.shop_name.trim()) { setError('Shop name is required'); return }
    setSaving(true); setError('')
    try {
      const res = await api.post('/shops/admin/create', {
        owner_id:    shopForm.owner_id,
        name:        shopForm.shop_name.trim(),
        category:    shopForm.category,
        description: shopForm.description,
        location:    shopForm.city,
        phone:       shopForm.phone,
        tin:         shopForm.tin,
        lat:         shopForm.lat  || null,
        lng:         shopForm.lng  || null,
        formatted_address: shopForm.place_name ||
          [shopForm.address_line1, shopForm.city, shopForm.province, shopForm.country].filter(Boolean).join(', '),
        address_line1:  shopForm.address_line1,
        city:           shopForm.city,
        province:       shopForm.province,
        country:        shopForm.country,
        delivery_zones:        shopForm.delivery_zones,
        fallback_delivery_fee: shopForm.fallback_delivery_fee !== '' ? Number(shopForm.fallback_delivery_fee) : null,
      })
      setCreatedShop(res.data.shop)
      setStep(2)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create shop')
    } finally { setSaving(false) }
  }

  // ── Step 2: Products ───────────────────────────────────────────────────────
  const [products,     setProducts]     = useState([])
  const [showProdForm, setShowProdForm] = useState(true)
  const [prodSaving,   setProdSaving]   = useState(false)
  const [prodError,    setProdError]    = useState('')
  const [prodForm,     setProdForm]     = useState({ name:'', description:'', category:'', price:'', stock:'', has_variants:false })
  const [prodOptions,  setProdOptions]  = useState([{ label:'', attrs:{}, price:'', quantity:'' }])
  const [prodImages,   setProdImages]   = useState([])
  const [prodPreviews, setProdPreviews] = useState([])
  const setProd = (k, v) => setProdForm(f => ({ ...f, [k]: v }))
  const prodPrevCatRef = useRef(prodForm.category)

  const selCat   = categories.find(c => c.name === prodForm.category)
  const attrDefs = (() => { try { return Array.isArray(selCat?.attribute_defs) ? selCat.attribute_defs : JSON.parse(selCat?.attribute_defs || '[]') } catch { return [] } })()

  useEffect(() => {
    if (prodPrevCatRef.current !== prodForm.category) {
      prodPrevCatRef.current = prodForm.category
      setProdOptions([{ label:'', attrs:{}, price:'', quantity:'' }])
    }
  }, [prodForm.category])

  const addProdOption    = () => setProdOptions(o => [...o, { label:'', attrs:{}, price:'', quantity:'' }])
  const updateProdOption = (i, val) => setProdOptions(o => o.map((x, idx) => idx === i ? val : x))
  const deleteProdOption = (i) => setProdOptions(o => o.filter((_, idx) => idx !== i))

  const handleAddProduct = async (e) => {
    e.preventDefault()
    if (!prodForm.name.trim())                                                          { setProdError('Product name is required.'); return }
    if (!prodForm.description.trim())                                                   { setProdError('Product description is required.'); return }
    if (prodImages.length === 0)                                                        { setProdError('At least one image is required.'); return }
    if (!prodForm.has_variants && (!prodForm.price || Number(prodForm.price) <= 0))    { setProdError('A valid price is required.'); return }
    if (prodForm.has_variants) {
      if (!prodOptions.length)                                                          { setProdError('Add at least one option.'); return }
      if (prodOptions.some(o => !o.label?.trim()))                                     { setProdError('Every option needs a label.'); return }
      if (prodOptions.some(o => !o.price || Number(o.price) <= 0))                    { setProdError('Every option needs a valid price.'); return }
    }
    setProdSaving(true); setProdError('')
    try {
      const fd = new FormData()
      fd.append('shop_id',      createdShop.shop_id)
      fd.append('name',         prodForm.name.trim())
      fd.append('description',  prodForm.description.trim())
      fd.append('category',     prodForm.category || '')
      fd.append('product_type', 'standard')
      fd.append('has_variants', String(prodForm.has_variants))
      if (!prodForm.has_variants) {
        fd.append('price', Number(prodForm.price))
        fd.append('stock', Number(prodForm.stock) || 0)
      } else {
        const minPrice   = Math.min(...prodOptions.map(o => Number(o.price) || 0))
        const totalStock = prodOptions.reduce((s, o) => s + (Number(o.quantity) || 0), 0)
        fd.append('price',    minPrice)
        fd.append('stock',    totalStock)
        fd.append('variants', JSON.stringify(prodOptions.map(o => ({
          attributes: { label: o.label.trim() },
          price:      Number(o.price)    || 0,
          quantity:   Number(o.quantity) || 0,
        }))))
      }
      prodImages.forEach(f => fd.append('images', f))
      const res = await api.post('/products/admin/add', fd, { headers:{ 'Content-Type':'multipart/form-data' } })
      setProducts(prev => [...prev, res.data.product])
      setProdForm({ name:'', description:'', category:'', price:'', stock:'', has_variants:false })
      setProdOptions([{ label:'', attrs:{}, price:'', quantity:'' }])
      setProdImages([])
      setProdPreviews([])
      setShowProdForm(false)
    } catch (err) { setProdError(err.response?.data?.error || 'Failed to add product') }
    finally { setProdSaving(false) }
  }

  const INP = { width:'100%', height:38, borderRadius:8, border:'1px solid #e9e9e4', padding:'0 12px', fontSize:13, outline:'none', fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box', background:'#fff' }
  const LBL = { display:'block', fontSize:11, fontWeight:700, color:'#111317', textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'JetBrains Mono',monospace", marginBottom:6 }

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.28)', zIndex:400 }} />
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:620, maxHeight:'92vh', background:'#fff', borderRadius:16, zIndex:401, display:'flex', flexDirection:'column', boxShadow:'0 24px 80px rgba(0,0,0,0.18)', animation:'popIn 0.2s ease', overflow:'hidden' }}>
        <style>{`@keyframes popIn{from{opacity:0;transform:translate(-50%,-48%) scale(0.97)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>

        {/* Header */}
        <div style={{ padding:'18px 24px', borderBottom:'1px solid #e9e9e4', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800 }}>
              {step === 1 ? '🏪 Create New Shop' : `📦 Add Products to ${createdShop?.shop_name}`}
            </div>
            <div style={{ fontSize:11, color:'#9a9a94', marginTop:2, fontFamily:"'JetBrains Mono',monospace" }}>
              Step {step} of 2 — {step === 1 ? 'Shop details' : 'Add products (optional)'}
            </div>
          </div>
          <div style={{ display:'flex', gap:6, marginRight:8 }}>
            {[1,2].map(s => (
              <div key={s} style={{ width:8, height:8, borderRadius:'50%', background: s <= step ? '#e8440a' : '#e9e9e4', transition:'background 0.2s' }} />
            ))}
          </div>
          <div onClick={onClose} style={{ cursor:'pointer', fontSize:20, color:'#9a9a94' }}>×</div>
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:'20px 24px' }}>

          {/* ══ STEP 1 ══ */}
          {step === 1 && (
            <form onSubmit={handleCreateShop} style={{ display:'flex', flexDirection:'column', gap:16 }}>

              {/* ── Owner search ── */}
              <div>
                <label style={LBL}>Owner (User) *</label>
                {shopForm.owner_id ? (
                  /* Selected owner card */
                  <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 14px', background:'#f0fdf4', border:'1.5px solid #bbf7d0', borderRadius:10 }}>
                    <div style={{ width:40, height:40, borderRadius:10, background:avatarBg(shopForm.owner_name||''), display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:800, flexShrink:0 }}>
                      {initials(shopForm.owner_name || shopForm.owner_phone)}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:800 }}>{shopForm.owner_name || 'User'}</div>
                      <div style={{ display:'flex', gap:8, marginTop:3, flexWrap:'wrap' }}>
                        <span style={{ fontSize:12, fontWeight:800, color:'#e8440a', fontFamily:"'JetBrains Mono',monospace", background:'#fff3f0', border:'1px solid #fdd0c0', padding:'1px 7px', borderRadius:5 }}>
                          📞 {shopForm.owner_phone}
                        </span>
                        <span style={{ fontSize:11, color:'#15803d', fontFamily:"'JetBrains Mono',monospace" }}>{shopForm.owner_id_display}</span>
                      </div>
                    </div>
                    <span style={{ fontSize:16 }}>✓</span>
                    <button type="button"
                      onClick={() => { setShop('owner_id',null); setShop('owner_name',''); setShop('owner_phone',''); setShop('owner_id_display',''); setShop('owner_search',''); setUserResults([]) }}
                      style={{ border:'none', background:'#fef2f2', color:'#b91c1c', borderRadius:6, padding:'4px 10px', fontSize:12, cursor:'pointer', fontWeight:700 }}>
                      Change
                    </button>
                  </div>
                ) : (
                  /* Search input + dropdown */
                  <div style={{ position:'relative' }}>
                    <input
                      value={shopForm.owner_search}
                      onChange={e => { setShop('owner_search', e.target.value); searchUsers(e.target.value) }}
                      placeholder="Type phone number, username or name…"
                      style={{ ...INP, paddingRight: userSearchLoad ? 36 : 12 }}
                      autoComplete="off"
                    />
                    {userSearchLoad && (
                      <div style={{ position:'absolute', right:12, top:10, fontSize:12, color:'#9a9a94' }}>…</div>
                    )}

                    {/* Results dropdown */}
                    {userResults.length > 0 && (
                      <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, boxShadow:'0 10px 30px rgba(0,0,0,0.12)', zIndex:30, maxHeight:240, overflowY:'auto' }}>
                        {userResults.map(u => (
                          <div key={u.user_id}
                            onMouseDown={e => e.preventDefault()}  // prevent blur before click
                            onClick={() => {
                              setShop('owner_id',          u.user_id)
                              setShop('owner_name',        u.username || u.name || '')
                              setShop('owner_phone',       u.phone    || '')
                              setShop('owner_id_display',  `#${u.user_id}`)
                              setShop('owner_search',      '')
                              setShop('phone', shopForm.phone || u.phone || '')
                              setUserResults([])
                            }}
                            style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 14px', cursor:'pointer', borderBottom:'1px solid #f3f3f0' }}
                            onMouseEnter={e => e.currentTarget.style.background='#f9f9f7'}
                            onMouseLeave={e => e.currentTarget.style.background='transparent'}>

                            {/* Avatar */}
                            <div style={{ width:38, height:38, borderRadius:9, background:avatarBg(u.username||u.phone||''), display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, flexShrink:0 }}>
                              {initials(u.username || u.phone)}
                            </div>

                            {/* Info */}
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontSize:13, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                {u.username || u.name || 'User'}
                              </div>
                              <div style={{ display:'flex', gap:6, marginTop:3, flexWrap:'wrap', alignItems:'center' }}>
                                {/* Phone — most prominent */}
                                <span style={{ fontSize:12, fontWeight:800, color:'#e8440a', fontFamily:"'JetBrains Mono',monospace", background:'#fff3f0', border:'1px solid #fdd0c0', padding:'1px 7px', borderRadius:5 }}>
                                  📞 {u.phone}
                                </span>
                                <span style={{ fontSize:10, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>
                                  #{u.user_id}
                                </span>
                                <span style={{ fontSize:10, fontWeight:600, color: u.role === 'seller' ? '#7c3aed' : '#6b7280', background: u.role === 'seller' ? '#f5f3ff' : '#f3f4f6', padding:'1px 6px', borderRadius:4, fontFamily:"'JetBrains Mono',monospace" }}>
                                  {u.role}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* No results hint */}
                    {shopForm.owner_search.length >= 2 && !userSearchLoad && userResults.length === 0 && (
                      <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:'#fff', border:'1px solid #e9e9e4', borderRadius:8, padding:'14px', fontSize:12, color:'#9a9a94', textAlign:'center', zIndex:30 }}>
                        No users found for "{shopForm.owner_search}"
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Shop name + category */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={LBL}>Shop Name *</label>
                  <input required value={shopForm.shop_name} onChange={e => setShop('shop_name', e.target.value)} placeholder="e.g. Kigali Boutique" style={INP} />
                </div>
                <div>
                  <label style={LBL}>Category</label>
                  <select value={shopForm.category} onChange={e => setShop('category', e.target.value)} style={{ ...INP, cursor:'pointer' }}>
                    <option value="">Select category…</option>
                    {categories.map(c => <option key={c.category_id} value={c.name}>{c.icon} {c.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label style={LBL}>Description</label>
                <textarea value={shopForm.description} onChange={e => setShop('description', e.target.value)}
                  placeholder="Brief shop description…"
                  style={{ ...INP, height:68, padding:'8px 12px', resize:'vertical' }} />
              </div>

              {/* Phone + TIN */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={LBL}>Shop Phone</label>
                  <input value={shopForm.phone} onChange={e => setShop('phone', e.target.value)} placeholder="078XXXXXXX" style={INP} />
                </div>
                <div>
                  <label style={LBL}>TIN (optional)</label>
                  <input value={shopForm.tin} onChange={e => setShop('tin', e.target.value)} placeholder="Tax number" style={INP} />
                </div>
              </div>

              {/* Fallback delivery fee */}
              <div>
                <label style={LBL}>Fallback Delivery Fee (RWF)</label>
                <input type="number" min="0" value={shopForm.fallback_delivery_fee}
                  onChange={e => setShop('fallback_delivery_fee', e.target.value)}
                  placeholder="e.g. 2000 — used when buyer address has no matching zone"
                  style={INP} />
                <div style={{ fontSize:11, color:'#9a9a94', marginTop:4 }}>Applied when buyer's address doesn't match any delivery zone</div>
              </div>

              {/* ── Google Maps location ── */}
              <div>
                <label style={LBL}>📍 Shop Location</label>

                {mapError ? (
                  <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:'10px 13px', fontSize:12, color:'#b91c1c' }}>
                    ⚠ {mapError}
                  </div>
                ) : (
                  <>
                    {/* Google Places input */}
                    <div style={{ position:'relative' }}>
                      <input
                        ref={mapInputRef}
                        defaultValue={shopForm.map_query}
                        placeholder={mapReady ? 'Search address e.g. KG 7 Ave, Kigali…' : 'Loading Google Maps…'}
                        disabled={!mapReady}
                        style={{ ...INP, paddingRight:36, color: mapReady ? '#111317' : '#9a9a94' }}
                      />
                      {shopForm.lat && (
                        <span onClick={clearLocation}
                          style={{ position:'absolute', right:10, top:9, cursor:'pointer', color:'#9a9a94', fontSize:16, lineHeight:1 }}>×</span>
                      )}
                    </div>

                    {/* Coordinates pill */}
                    {shopForm.lat && shopForm.lng && (
                      <div style={{ marginTop:6, display:'inline-flex', alignItems:'center', gap:6, background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:6, padding:'3px 10px', fontSize:11, color:'#15803d', fontFamily:"'JetBrains Mono',monospace" }}>
                        📍 {Number(shopForm.lat).toFixed(5)}, {Number(shopForm.lng).toFixed(5)}
                        <span style={{ color:'#9a9a94', maxWidth:260, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          — {shopForm.place_name?.split(',').slice(0,2).join(',')}
                        </span>
                      </div>
                    )}

                    {/* Google Map preview */}
                    <div
                      ref={mapPreviewRef}
                      style={{
                        marginTop:10, borderRadius:10, overflow:'hidden',
                        border:'1px solid #e9e9e4',
                        height: shopForm.lat ? 220 : 0,
                        transition:'height 0.3s ease',
                        display: shopForm.lat ? 'block' : 'none',
                      }}
                    />
                  </>
                )}
              </div>

              {/* Address fields */}
              <div>
                <label style={LBL}>Shop Address</label>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  <input value={shopForm.address_line1} onChange={e => setShop('address_line1', e.target.value)}
                    placeholder="Street / building (e.g. KG 7 Ave, House 12)"
                    style={INP} />
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                    <div>
                      <div style={{ fontSize:10, fontWeight:700, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", marginBottom:4, textTransform:'uppercase' }}>City</div>
                      <input value={shopForm.city} onChange={e => setShop('city', e.target.value)} placeholder="Kigali" style={INP} />
                    </div>
                    <div>
                      <div style={{ fontSize:10, fontWeight:700, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", marginBottom:4, textTransform:'uppercase' }}>Province</div>
                      <select value={shopForm.province} onChange={e => setShop('province', e.target.value)} style={{ ...INP, cursor:'pointer' }}>
                        {['Kigali City','Eastern Province','Western Province','Northern Province','Southern Province'].map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize:10, fontWeight:700, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", marginBottom:4, textTransform:'uppercase' }}>Country</div>
                      <input value={shopForm.country} onChange={e => setShop('country', e.target.value)} placeholder="Rwanda" style={INP} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery Zones */}
              <div>
                <label style={LBL}>Delivery Zones</label>
                <ZonePicker
                  zones={deliveryZones}
                  selected={shopForm.delivery_zones}
                  onChange={ids => setShop('delivery_zones', ids)}
                />
              </div>

              {error && (
                <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:7, padding:'9px 13px', fontSize:12, color:'#b91c1c', fontWeight:600 }}>⚠ {error}</div>
              )}

              <div style={{ display:'flex', gap:10, paddingTop:4 }}>
                <button type="button" onClick={onClose}
                  style={{ flex:1, height:42, borderRadius:8, border:'1px solid #e9e9e4', background:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  style={{ flex:2, height:42, borderRadius:8, border:'none', background:saving?'#f0c4b4':'#e8440a', color:'#fff', fontSize:13, fontWeight:700, cursor:saving?'not-allowed':'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                  {saving ? 'Creating…' : 'Create Shop →'}
                </button>
              </div>
            </form>
          )}

          {/* ══ STEP 2: PRODUCTS ══ */}
          {step === 2 && (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {products.length > 0 && (
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'JetBrains Mono',monospace", marginBottom:8 }}>Added ({products.length})</div>
                  {products.map((p, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, marginBottom:6 }}>
                      <span>📦</span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:700 }}>{p.name}</div>
                        <div style={{ fontSize:11, color:'#15803d', fontFamily:"'JetBrains Mono',monospace" }}>
                          {p.has_variants ? `${p.quantity} units across variants` : `RWF ${Number(p.price).toLocaleString()} · ${p.quantity} in stock`}
                        </div>
                      </div>
                      <span style={{ fontSize:12, color:'#15803d', fontWeight:700 }}>✓</span>
                    </div>
                  ))}
                </div>
              )}

              {showProdForm ? (
                <form onSubmit={handleAddProduct} style={{ background:'#f9f9f7', border:'1px solid #e9e9e4', borderRadius:12, padding:'16px', display:'flex', flexDirection:'column', gap:14 }}>
                  <div style={{ fontSize:13, fontWeight:800 }}>{products.length > 0 ? 'Add Another Product' : 'Add a Product'}</div>

                  <div>
                    <label style={LBL}>Product Name *</label>
                    <input required value={prodForm.name} onChange={e => setProd('name', e.target.value)} placeholder="e.g. Summer Dress" style={INP} />
                  </div>
                  <div>
                    <label style={LBL}>Description *</label>
                    <textarea value={prodForm.description} onChange={e => setProd('description', e.target.value)}
                      placeholder="Describe the product — material, size, colour, usage…"
                      style={{ ...INP, height:64, padding:'8px 12px', resize:'vertical' }} />
                  </div>

                  {/* Product category — independent from shop category */}
                  <div>
                    <label style={LBL}>Product Category</label>
                    <select value={prodForm.category} onChange={e => setProd('category', e.target.value)}
                      style={{ ...INP, cursor:'pointer' }}>
                      <option value="">Select category…</option>
                      {categories.map(c => (
                        <option key={c.category_id} value={c.name}>{c.icon} {c.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Product structure */}
                  <div style={{ display:'flex', gap:8 }}>
                    {[
                      { val:false, label:'📦 Single Product', desc:'One price + stock' },
                      { val:true,  label:'🎨 Has Options',    desc:'Sizes / colors etc.' },
                    ].map(opt => (
                      <div key={String(opt.val)} onClick={() => setProd('has_variants', opt.val)}
                        style={{ flex:1, padding:'10px 12px', borderRadius:10, border:`1.5px solid ${prodForm.has_variants===opt.val?'#e8440a':'#e9e9e4'}`, background:prodForm.has_variants===opt.val?'#fff3f0':'#fff', cursor:'pointer', textAlign:'center', transition:'all 0.12s' }}>
                        <div style={{ fontSize:13, fontWeight:700, color:prodForm.has_variants===opt.val?'#e8440a':'#374151' }}>{opt.label}</div>
                        <div style={{ fontSize:11, color:'#9a9a94', marginTop:2 }}>{opt.desc}</div>
                      </div>
                    ))}
                  </div>

                  {!prodForm.has_variants && (
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                      <div>
                        <label style={LBL}>Price (RWF) *</label>
                        <input type="number" min="1" required value={prodForm.price} onChange={e => setProd('price', e.target.value)} placeholder="0" style={INP} />
                      </div>
                      <div>
                        <label style={LBL}>Stock Quantity</label>
                        <input type="number" min="0" value={prodForm.stock} onChange={e => setProd('stock', e.target.value)} placeholder="0" style={INP} />
                      </div>
                    </div>
                  )}

                  {prodForm.has_variants && (
                    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <label style={{ ...LBL, marginBottom:0 }}>
                          Options ({prodOptions.length})
                          {attrDefs.length > 0 && <span style={{ marginLeft:8, fontSize:10, fontWeight:600, color:'#e8440a', textTransform:'none', letterSpacing:0 }}>— {selCat?.name}</span>}
                        </label>
                        <span style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>
                          {prodOptions.reduce((s, o) => s + (Number(o.quantity) || 0), 0)} units total
                        </span>
                      </div>
                      {!prodForm.category && (
                        <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, padding:'9px 13px', fontSize:12, color:'#92400e' }}>
                          ℹ Select a category above to see attribute-based option selectors.
                        </div>
                      )}
                      {prodOptions.map((o, i) => (
                        <OptionRow key={i} option={o} index={i} attrDefs={attrDefs} onChange={updateProdOption} onDelete={deleteProdOption} />
                      ))}
                      <button type="button" onClick={addProdOption}
                        style={{ height:34, borderRadius:7, border:'1px dashed #e8440a', background:'transparent', color:'#e8440a', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                        + Add Option
                      </button>
                    </div>
                  )}

                  {/* Images */}
                  <div>
                    <label style={LBL}>
                      Images *
                      <span style={{ marginLeft:8, fontSize:10, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", color: prodImages.length===0 ? '#b91c1c' : '#15803d' }}>
                        {prodImages.length}/5
                      </span>
                    </label>
                    <div style={{ background:'#fff', border:`1px solid ${prodImages.length===0?'#fecaca':'#e9e9e4'}`, borderRadius:10, padding:'12px' }}>
                      {prodPreviews.length > 0 && (
                        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:10 }}>
                          {prodPreviews.map((url, i) => (
                            <div key={i} style={{ position:'relative' }}>
                              <img src={url} alt="" style={{ width:72, height:72, borderRadius:8, objectFit:'cover', border:'2px solid #bae6fd', display:'block' }} />
                              <button type="button" onClick={() => {
                                URL.revokeObjectURL(prodPreviews[i])
                                setProdImages(p => p.filter((_, idx) => idx !== i))
                                setProdPreviews(p => p.filter((_, idx) => idx !== i))
                              }} style={{ position:'absolute', top:-6, right:-6, width:20, height:20, borderRadius:'50%', border:'none', background:'#ef4444', color:'#fff', fontSize:11, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900 }}>×</button>
                            </div>
                          ))}
                        </div>
                      )}
                      {prodImages.length < 5 && (
                        <label style={{ display:'inline-flex', alignItems:'center', gap:6, background: prodImages.length===0 ? '#e8440a' : '#f3f3f0', color: prodImages.length===0 ? '#fff' : '#374151', padding:'7px 14px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer', border: prodImages.length===0 ? 'none' : '1px dashed #d1d5db' }}>
                          📷 {prodImages.length===0 ? 'Add Images *' : `Add More (${5-prodImages.length} left)`}
                          <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp" multiple style={{ display:'none' }} onChange={e => {
                            const files = Array.from(e.target.files || []).slice(0, 5 - prodImages.length)
                            setProdImages(p => [...p, ...files])
                            setProdPreviews(p => [...p, ...files.map(f => URL.createObjectURL(f))])
                            e.target.value = ''
                          }} />
                        </label>
                      )}
                      {prodImages.length === 0 && <div style={{ marginTop:8, fontSize:11, color:'#b91c1c', fontWeight:600 }}>⚠ At least one image is required</div>}
                    </div>
                  </div>

                  {prodError && <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:7, padding:'9px 13px', fontSize:12, color:'#b91c1c', fontWeight:600 }}>⚠ {prodError}</div>}

                  <div style={{ display:'flex', gap:10 }}>
                    {products.length > 0 && (
                      <button type="button" onClick={() => setShowProdForm(false)}
                        style={{ flex:1, height:38, borderRadius:8, border:'1px solid #e9e9e4', background:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
                    )}
                    <button type="submit" disabled={prodSaving || prodImages.length === 0}
                      style={{ flex:2, height:38, borderRadius:8, border:'none', background:(prodSaving||prodImages.length===0)?'#f0c4b4':'#e8440a', color:'#fff', fontSize:12, fontWeight:700, cursor:(prodSaving||prodImages.length===0)?'not-allowed':'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                      {prodSaving ? 'Adding…' : '+ Add Product'}
                    </button>
                  </div>
                </form>
              ) : (
                <button onClick={() => setShowProdForm(true)}
                  style={{ height:42, borderRadius:9, border:'1px dashed #e8440a', background:'transparent', color:'#e8440a', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                  + Add Another Product
                </button>
              )}

              <div style={{ display:'flex', gap:10, paddingTop:4, borderTop:'1px solid #e9e9e4', marginTop:4 }}>
                <button onClick={() => onCreated(createdShop)}
                  style={{ flex:1, height:42, borderRadius:8, border:'1px solid #e9e9e4', background:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", color:'#6b7280' }}>
                  {products.length > 0 ? 'Done — Skip More' : 'Skip — No Products'}
                </button>
                {products.length > 0 && (
                  <button onClick={() => onCreated(createdShop)}
                    style={{ flex:2, height:42, borderRadius:8, border:'none', background:'#15803d', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                    ✓ Finish — {products.length} Product{products.length>1?'s':''} Added
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Sellers() {
  const [shops,        setShops]        = useState([])
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState('')
  const [searchInput,  setSearchInput]  = useState('')
  const [search,       setSearch]       = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page,         setPage]         = useState(1)
  const [totalPages,   setTotalPages]   = useState(1)
  const [totalShops,   setTotalShops]   = useState(0)
  const [stats,        setStats]        = useState({ total: 0, active: 0, pending: 0, suspended: 0 })
  const [selected,     setSelected]     = useState(null)
  const [revoking,     setRevoking]     = useState(null)
  const [revokeLoad,   setRevokeLoad]   = useState(false)
  const [showCreate,     setShowCreate]     = useState(false)  // create shop modal
  const [categories,     setCategories]     = useState([])     // for dropdowns
  const [deliveryZones,  setDeliveryZones]  = useState([])     // global delivery zones

  const PER_PAGE = 15
  const fetchIdRef = useRef(0)

  // Debounce search input → only fetch after 400ms of no typing
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  const fetchShops = useCallback(async () => {
    const id = ++fetchIdRef.current
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({
        page, limit: PER_PAGE,
        ...(search             && { search }),
        ...(statusFilter !== '' && { status: statusFilter }),
      })
      const res = await api.get(`/shops/admin/shops?${params}`)
      if (id !== fetchIdRef.current) return
      setShops(res.data.shops      || [])
      setTotalPages(res.data.totalPages || 1)
      setTotalShops(res.data.total      || 0)
      setStats(res.data.stats      || { total: 0, active: 0, pending: 0, suspended: 0 })
    } catch {
      if (id !== fetchIdRef.current) return
      setError('Failed to load shops')
      const mock = Array.from({ length: 10 }, (_, i) => ({
        shop_id: i + 1, shop_name: `Shop ${i + 1}`,
        owner_name: `Owner ${i + 1}`, owner_phone: `078${String(i+1).padStart(7,'0')}`,
        owner_email: `owner${i+1}@example.com`, owner_id: i + 10,
        category: ['Fashion','Tech','Beauty','Food','Sports'][i % 5],
        status: [1, 1, 2, 0, 1][i % 5],
        product_count: Math.floor(Math.random() * 50),
        order_count:   Math.floor(Math.random() * 200),
        revenue:       Math.random() * 10000,
        created_at:    new Date(Date.now() - i * 86400000 * 14).toISOString(),
        products: Array.from({ length: 3 }, (_, j) => ({
          product_id: j+1, name: `Product ${j+1}`, price: (j+1)*25,
          quantity: j===1 ? 0 : 10, category: 'General', images: null,
          product_type: j === 0 ? 'quick_live' : 'standard',
        })),
        addresses: [{ formatted_address: 'KG 7 Ave, Kigali, Rwanda' }],
      }))
      setShops(mock)
      setTotalPages(2)
      setTotalShops(20)
      setStats({ total: 20, active: 14, pending: 4, suspended: 2 })
    } finally {
      if (id === fetchIdRef.current) setLoading(false)
    }
  }, [page, search, statusFilter])

  useEffect(() => { fetchShops() }, [fetchShops])
  useEffect(() => { setPage(1)  }, [search, statusFilter])
  useEffect(() => {
    api.get('/settings/categories').then(r => setCategories(r.data.categories || [])).catch(() => {})
    api.get('/settings/admin/delivery-zones').then(r => setDeliveryZones(r.data.zones || [])).catch(() => {})
  }, [])

  const updateShopStatus = async (shop, newStatus) => {
    try {
      await api.patch(`/shops/admin/shops/${shop.shop_id}/status`, { status: newStatus })
      setShops(prev => prev.map(s => s.shop_id === shop.shop_id ? { ...s, status: newStatus } : s))
      if (selected?.shop_id === shop.shop_id) setSelected(prev => ({ ...prev, status: newStatus }))
    } catch {
      alert('Failed to update shop status')
    }
  }

  const handleRevoke = async () => {
    if (!revoking) return
    setRevokeLoad(true)
    try {
      await api.patch(`/shops/admin/shops/${revoking.shop_id}/revoke`)
      setShops(prev => prev.map(s => s.shop_id === revoking.shop_id ? { ...s, status: 0 } : s))
      setRevoking(null)
      setSelected(null)
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to revoke seller role')
    } finally {
      setRevokeLoad(false)
    }
  }

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ marginBottom: 24, display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Shop & Seller Management</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Approve, manage and revoke seller access across the platform.</div>
        </div>
        <button onClick={() => setShowCreate(true)}
          style={{ height:40, padding:'0 18px', borderRadius:9, border:'none', background:'#e8440a', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:'0 2px 8px rgba(232,68,10,0.2)', display:'flex', alignItems:'center', gap:8 }}>
          + Create Shop
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
        <StatCard label="Total Shops"  value={stats.total.toLocaleString()}     />
        <StatCard label="Active"       value={stats.active.toLocaleString()}    accent="#15803d" />
        <StatCard label="Pending"      value={stats.pending.toLocaleString()}   accent="#b45309" />
        <StatCard label="Suspended"    value={stats.suspended.toLocaleString()} accent="#b91c1c" />
      </div>

      {/* Pending alert */}
      {stats.pending > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderLeft: '3px solid #f59e0b', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>⚠️</span>
          <span><b>{stats.pending} shop{stats.pending > 1 ? 's' : ''} pending approval</b></span>
          <span onClick={() => setStatusFilter('2')} style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#b45309', cursor: 'pointer', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>View Pending →</span>
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 12px' }}>
          <span style={{ fontSize: 14, color: '#9a9a94' }}>🔍</span>
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search by shop name, owner, phone or category…" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111317', flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
          {searchInput && <span onClick={() => { setSearchInput(''); setSearch('') }} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 16 }}>×</span>}
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
          {FILTER_OPTIONS.map(o => <option key={o.label} value={o.value}>{o.label}</option>)}
        </select>
        <button onClick={fetchShops} style={{ height: 36, width: 36, borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↻</button>
        <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>{totalShops.toLocaleString()} shops</div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#b91c1c' }}>⚠ {error} — showing mock data</div>
      )}

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.6fr 1.2fr 0.7fr 0.7fr 0.9fr 0.9fr 0.8fr', padding: '10px 20px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
          {['Shop','Owner','Category','Products','Orders','Gross Sales','Status','Actions'].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading shops…</div>
        ) : shops.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No shops found.</div>
        ) : (
          shops.map((shop, i) => {
            const bg = avatarBg(shop.shop_name || '')
            return (
              <div
                key={shop.shop_id}
                onClick={() => setSelected(shop)}
                style={{ display: 'grid', gridTemplateColumns: '2fr 1.6fr 1.2fr 0.7fr 0.7fr 0.9fr 0.9fr 0.8fr', padding: '13px 20px', borderBottom: i < shops.length - 1 ? '1px solid #f3f3f0' : 'none', cursor: 'pointer', transition: 'background 0.12s', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 8, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: '#111317', flexShrink: 0, overflow: 'hidden' }}>
                    {shopAvatarUrl(shop.shop_avatar)
                      ? <img src={shopAvatarUrl(shop.shop_avatar)} alt={shop.shop_name} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => { e.target.style.display='none'; e.target.parentNode.dataset.fallback='1' }} />
                      : initials(shop.shop_name)
                    }
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>{shop.shop_name}</div>
                    <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{shop.shop_id}</div>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{shop.owner_name || '—'}</div>
                  <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{shop.owner_phone}</div>
                </div>
                <div style={{ fontSize: 12 }}>{shop.category || '—'}</div>
                <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{shop.product_count ?? '—'}</div>
                <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{shop.order_count ?? '—'}</div>
                <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, color: '#15803d' }}>{money(shop.revenue)}</div>
                <div><StatusBadge status={shop.status} /></div>
                <div style={{ display: 'flex', gap: 5 }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => setSelected(shop)} style={{ padding: '4px 9px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>View</button>
                  {shop.status === 2 && <button onClick={() => updateShopStatus(shop, 1)} style={{ padding: '4px 9px', borderRadius: 6, border: 'none', background: '#f0fdf4', color: '#15803d', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Approve</button>}
                  {shop.status === 1 && <button onClick={() => updateShopStatus(shop, 0)} style={{ padding: '4px 9px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Suspend</button>}
                  {shop.status === 0 && <button onClick={() => updateShopStatus(shop, 1)} style={{ padding: '4px 9px', borderRadius: 6, border: 'none', background: '#f0fdf4', color: '#15803d', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Restore</button>}
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>Page {page} of {totalPages}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
            })}
            <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}

      <ShopDrawer
        shop={selected}
        categories={categories}
        deliveryZones={deliveryZones}
        onClose={() => setSelected(null)}
        onStatusChange={updateShopStatus}
        onRevoke={s => { setRevoking(s); setSelected(null) }}
      />

      <RevokeModal
        shop={revoking}
        onClose={() => setRevoking(null)}
        onConfirm={handleRevoke}
        loading={revokeLoad}
      />

      {showCreate && (
        <CreateShopModal
          categories={categories}
          deliveryZones={deliveryZones}
          onClose={() => setShowCreate(false)}
          onCreated={(newShop) => {
            setShops(prev => [newShop, ...prev])
            setStats(prev => ({ ...prev, total: prev.total + 1, active: prev.active + 1 }))
            setShowCreate(false)
          }}
        />
      )}
    </div>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ minWidth: 34, height: 34, borderRadius: 7, border: '1px solid #e9e9e4', background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#9a9a94' : '#111317', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', padding: '0 10px', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: disabled ? 0.4 : 1 }}>
      {label}
    </button>
  )
}